import React, { useState } from 'react';
import { ctsIconEntries } from './ctsToolbarButtons';
import AdminPanelDialog from './AdminPanelDialog';
import ChartAnalysisButton from './ChartAnalysisButton';
import GlobalScanButton from './GlobalScanButton';
/*
 * ⚠ `isRelocated` IS NO LONGER IMPORTED HERE, and the function stays in place.
 *
 * Relocation was one of the four subtractive gates this loop used to consult. It is now expressed
 * by a manifest simply not naming a tool in its `toolbar` while naming it in an `adminPanel` tab
 * — one list per product rather than a deny list plus a per-product exemption table. The function
 * still backs `AdminPanelDialog` and its own tests, which is where the decision is recorded.
 */
import { ADMIN_PANEL_ACTION } from '../config/adminPanelTools';
import { useManifest } from '../context/ManifestContext';
import { actionIdsOf } from '../config/toolRegistry';
import { cycleIconEntries } from './cycleToolbarButtons';
import { useTheme } from '@mui/material/styles';
import legibleIcon from '../utils/legibleIcon';
import { hiddenIconIndices, SHORT } from '../utils/toolbarBarMode';
import { useChartOverlays } from '../context/ChartOverlayContext';
import PropTypes from 'prop-types';
import { Box, Tooltip, IconButton, Popover, CircularProgress } from '@mui/material';
import { textToolTooltip } from '../utils/textAnnotationSizes';
import { dispatchToolbarAction } from '../utils/actionDispatcher';
import { countOverlayGroups, lastOverlaySize } from '../utils/drawingStack';
import { CHART_VIEW, chartViewActionLabel, nextChartView } from '../utils/chartViewMode';
import MinimalMultiTabDialogTrigger from './MinimalMultiTabDialogTrigger';



import ShowChartIcon from '@mui/icons-material/ShowChart';
import BrushIcon from '@mui/icons-material/Brush';
import flaskIcon1 from '../assets/flask-1.png';
import flaskIcon2 from '../assets/flask-2.png';
import flaskIcon3 from '../assets/flask-3.png';
import flaskIcon4 from '../assets/flask-4.png';
import icon5 from '../assets/icon5.png';
import MOAStrategyDialog from './MOAStrategyDialog';
import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import PublicIcon from '@mui/icons-material/Public';
import ArchitectureIcon from '@mui/icons-material/Architecture';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import FunctionsIcon from '@mui/icons-material/Functions';
import ChangeHistoryIcon from '@mui/icons-material/ChangeHistory';
import DonutLargeIcon from '@mui/icons-material/DonutLarge';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import ClearIcon from '@mui/icons-material/Clear';
// The study-output drawer's glyph, shared with `MessagesPanelButton` in the header so one drawer
// is one icon wherever it is reached from.
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import TextDecreaseIcon from '@mui/icons-material/TextDecrease';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
// ── OPERATOR FEATURES icons (revertible as one commit) ───────────────────────────────────────────
import RuleIcon from '@mui/icons-material/Rule';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import PlayCircleFilledWhiteIcon from '@mui/icons-material/PlayCircleFilledWhite';
import TimelapseIcon from '@mui/icons-material/Timelapse';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import BarChartIcon from '@mui/icons-material/BarChart';
import MessagesPanelDialog from './MessagesPanelDialog';
import BarFloaterSelectionDialog, {
  barFloaterTooltipFor as barFloaterTooltip, barFloaterAriaLabelFor as barFloaterAriaLabel,
} from './BarFloaterSelectionDialog';
import ViewListIcon from '@mui/icons-material/ViewList';
import VedicAspectsStudyDialog from './VedicAspectsStudyDialog';
import { DEFAULT_DRAWING_COLOR } from '../utils/drawingColors';
import './TabPanel-toolbar-gradient.css';
import './TabPanel-tooltip.css';
/*
 * ⚠ NOTHING IS IMPORTED FROM `toolbarProfiles` ANY MORE, and that is the end of the subtractive
 * architecture in this file.
 *
 * `isActionVisibleForProfile` and `isActionDeniedByProfile` were the profile gate — a dev
 * dropdown's preview of another product's bar. `isActionVisibleForProduct` was `PRODUCT_DENIES`,
 * and it survived Stage 1 on one line: the astro catalog's own gate. A manifest answers both
 * questions directly, and the preview is now the MANIFEST preview, so a second consultation would
 * be a second answer — with the one that disagreed being the one nobody tested.
 */
import { showsFullCatalog, CATALOG_LAYOUT } from '../config/astroCatalog';
import { ACTIVE_PRODUCT } from '../config/productTarget';
import { isIconVisibleForRole } from '../config/nonAdminChrome';
import { tooltipForProduct } from '../utils/productTooltips';

const displayTooltips = true;

const ICONS = [
  // ... previous icons ...
  { component: '/images/longistudy.png', isImage: true, alt: 'Longitude Study', tooltip: 'Study of well known longitudinal aspects as well as aspects of any angular distance between any two planets', action: 'open_controls' },
  { component: '/images/Declination.png', isImage: true, alt: 'Declination Study', tooltip: 'Study of declination aspects such as parallel and contra-parallel between any two planets', action: 'open_declination_study' },
  { component: <BrushIcon />, tooltip: 'Highlight the area covered by specified degree difference of longitude between any two planets', action: 'open_special_studies' },
  { component: (
    <svg width="32" height="32" viewBox="0 0 32 32">
      <path d="M4 10 Q8 6, 12 10 T20 10 T28 10" stroke="#1976d2" strokeWidth="2" fill="none" />
      <path d="M4 16 Q8 12, 12 16 T20 16 T28 16" stroke="#388e3c" strokeWidth="2" fill="none" />
      <path d="M4 22 Q8 18, 12 22 T20 22 T28 22" stroke="#fbc02d" strokeWidth="2" fill="none" />
    </svg>
  ), tooltip: 'Draw lines of planetary longitude (with optional mapping to a price) to draw planetary Support and Resistance lines for the chart', action: 'open_planetary_lines_study' },
  { component: (
    // Three bodies converging on one averaged line: what the study computes, drawn as its icon.
    <svg width="32" height="32" viewBox="0 0 32 32">
      <circle cx="7" cy="8" r="2.2" fill="#7b1fa2" />
      <circle cx="7" cy="16" r="2.2" fill="#1976d2" />
      <circle cx="7" cy="24" r="2.2" fill="#388e3c" />
      <path d="M9 8 Q17 8, 20 16" stroke="#7b1fa2" strokeWidth="1.4" fill="none" />
      <path d="M9 16 L20 16" stroke="#1976d2" strokeWidth="1.4" fill="none" />
      <path d="M9 24 Q17 24, 20 16" stroke="#388e3c" strokeWidth="1.4" fill="none" />
      <path d="M20 16 L29 16" stroke="#c2185b" strokeWidth="2.4" fill="none" strokeLinecap="round" />
    </svg>
  ), tooltip: 'ALL - Average Longitude Lines: the circular mean of the longitudes of the selected bodies, plotted as a planetary line', action: 'open_average_longitude_study' },
  { component: <DoubleArrowIcon />, tooltip: 'This is not just based on longitude difference. Vertical lines are drawn only when the selected Planet-A follows the second selected Planet-B', action: 'open_planet_follow_study' },
  { component: (
    <svg width="32" height="32" viewBox="0 0 32 32">
      {/* Brush 1: Green handle, orange tip */}
      <rect x="8" y="8" width="3" height="14" rx="1" fill="#43a047" />
      <ellipse cx="9.5" cy="8" rx="2.2" ry="2.2" fill="#fb8c00" />
      <rect x="8" y="20" width="3" height="6" rx="1.2" fill="#cfd8dc" />
      {/* Brush 2: Purple handle, pink tip */}
      <rect x="18" y="8" width="3" height="14" rx="1" fill="#8e24aa" />
      <ellipse cx="19.5" cy="8" rx="2.2" ry="2.2" fill="#ec407a" />
      <rect x="18" y="20" width="3" height="6" rx="1.2" fill="#cfd8dc" />
    </svg>
  ), tooltip: 'Highlight the chart area when conditions selected in the study are met. This is an advanced strategy analyzer that decorates the chart as well as prepares summary of statistics of successful long and short trades', action: 'open_area_highlight_study' },
  { component: icon5, isImage: true, alt: 'Planet Characteristics Plot', tooltip: 'Plot longitude difference, declination difference, distance and speed of selected planets over the chart', action: 'open_planet_characteristics_dialog', size: 48, adminOnly: true },
  /*
    THE STUDY CLUSTER, IN A FIXED ORDER.

    Left to right: the two cycle presets, TrendPulse, the three Critical Timing Scans, then the
    spiral reversal scan. Specified rather than arrived at, so the ordering is a decision someone
    can disagree with rather than the order things happened to be added in.

    Alpha Replay used to close this group and moved out of it on 2026-10-20 — it is the only one
    that computed nothing, and it now stands beside the two simulator icons.

    Three of these are SPREADS of generated entries, so the group's shape here is not its shape at
    runtime. Nothing downstream depends on a position in this list - the short-bar preset finds the
    flask by its alt text, and the routing switch keys on the action - which is what makes moving
    them safe.
  */
  // The two quick-run cycle buttons. See cycleToolbarButtons.js.
  ...cycleIconEntries(),
  { component: (
    /*
      TrendPulse: a pulse train that resolves into a rising trend vector.

      Drawn rather than an image file, so it takes currentColor and stays crisp at the toolbar's
      48px and on a high-DPI screen - the bitmap it replaces was neither. The two halves are the
      study: a repeating cycle on the left, and where that cycle is pointing on the right.
    */
    <svg width="30" height="30" viewBox="0 0 32 32" aria-label="TrendPulse" focusable="false">
      <path d="M2 20 h4 l2.5 -8 l3 14 l3 -11 l2.5 5 h3"
        fill="none" stroke="currentColor" strokeWidth="2"
        strokeLinecap="round" strokeLinejoin="round" />
      <path d="M20 20 L26 11" fill="none" stroke="currentColor" strokeWidth="2.2"
        strokeLinecap="round" />
      <path d="M21 10.5 L27 10 L26.5 16" fill="none" stroke="currentColor" strokeWidth="2.2"
        strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ), alt: 'TrendPulse', tooltip: 'TrendPulse', action: 'run_trendpulse', size: 48, adminOnly: true },
  // ── ONE-CLICK ENGINE EXECUTION ──────────────────────────────────────────────────────────
  //
  // Three icons that RUN the engine and paint the result, rather than opening a dialog for the
  // reader to configure. Each uses that provider's own locked defaults -- the same parameters
  // every published figure was measured under -- so a click is reproducible and a chart drawn
  // this way can be compared against the record.
  //
  // ADMIN ONLY as of 2026-10-17, and the gate is NOT here — see ADMIN_ONLY_TOOLBAR_ACTIONS in
  // config/nonAdminChrome.js, which names all six of them in one list because two are generated
  // in another module. (The sixth is Alpha Replay, which no longer sits in this stretch of the
  // array — the list is a set of actions, not a span.) They remain the production surface for
  // the ASC and AI PROFILES, which is a different question from the signed-in user's role.
  { component: <PlayCircleOutlineIcon />, size: 48,
    tooltip: 'Run PTA — the Equator engine, with its locked parameters, painted on the chart',
    action: 'run_engine_pta' },
  { component: <PlayCircleFilledWhiteIcon />, size: 48,
    tooltip: 'Run PTB — the eight band rules, with their locked parameters, painted on the chart',
    action: 'run_engine_ptb' },
  { component: <TimelapseIcon />, size: 48,
    tooltip: 'Run CTS — the temporal pivot engine, with its locked parameters, painted on the chart',
    action: 'run_engine_cts' },
  // The three Critical Timing Scan buttons, one per orb. See ctsToolbarButtons.js.
  ...ctsIconEntries(),
  // A spiral, drawn rather than imported: the glyph has to read as "spiral" at 28px, and the
  // icon sets available here have no spiral. Two turns is enough to be unmistakable; more becomes
  // a smudge at this size.
  //
  // UNGATED as of 2026-10-17 — it was admin-only while the study was being evaluated and is now
  // the one scan a customer gets, standing where the six engine and cycle buttons used to be.
  // The VISIBILITY-only caveat that came with the gate holds in reverse: /api/spiral/scan and
  // /api/spiral/inspect were always public endpoints, so nothing about reachability changed.
  { component: (
    <svg width="28" height="28" viewBox="0 0 28 28">
      <path
        d="M14 14 m0 0 a2 2 0 1 1 2 -2 a5 5 0 1 1 -5 5 a8 8 0 1 1 8 -8 a11 11 0 1 1 -11 11"
        fill="none" stroke="#f57c00" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  // "the short cycle length" rather than "the lunar month", on instruction. The ENGINE is
  // unchanged — `CyclePeriod` still defaults to the synodic month and the scan still measures in
  // it — so this is the vocabulary constraint the cycle buttons already work under, reaching the
  // one tooltip that had been left speaking the study's own language instead of the product's.
  ), tooltip: 'Φ Reversal Scan - find where √F(n) multiples of the short cycle length from past pivots converge on a bar near now', action: 'spiral_reversal_scan' },
  // ALPHA REPLAY used to end this cluster and now sits beside the DynaMan Simulator, further down
  // this array — see the entry marked ALPHA REPLAY. It left because it is not a STUDY: everything
  // above computes something and draws it, and that one hides the future and hands the chart over
  // bar by bar, which is the question the two simulator icons ask.
  { component: <span style={{ width: 36, display: 'inline-block' }} />, tooltip: '', action: undefined },
  { component: flaskIcon1, isImage: true, alt: 'Strategy 1', tooltip: 'Create and execute a new strategy that is comprised of potentially many criteria of different types. This is an advanced topic and can generate insights that otherwise are not obvious', action: 'open_strategy_design_dialog', size: 48 },
  { component: flaskIcon2, isImage: true, alt: 'Strategy 2', tooltip: 'This Strategy Designer is similar to the previous one but is more focused on providing Vedic Astrology studies that can be combined to build your own strategy', action: 'open_strategy_2_design_dialog', size: 48 },
  { component: flaskIcon3, isImage: true, alt: 'Time and Event Strategy', tooltip: 'This dialog provides numerous studies to find if an event correlates leading to a market move in specified number of days and various combinations thereof', action: 'open_time_and_event_strategy_dialog', size: 48 },
  { component: flaskIcon4, isImage: true, alt: 'MOA Strategy Designer', tooltip: 'Mother Of All Strategy Designer — combine multiple strategy types into one powerful study', action: 'open_moa_strategy_dialog', size: 48 },
  { component: (
    <svg width="32" height="32" viewBox="0 0 32 32">
      <rect x="4" y="6" width="24" height="20" rx="2.5" fill="#fff" stroke="#1976d2" strokeWidth="1.5" />
      <line x1="4" y1="6" x2="28" y2="26" stroke="#388e3c" strokeWidth="1.2" />
      <line x1="28" y1="6" x2="4" y2="26" stroke="#388e3c" strokeWidth="1.2" />
      <polygon points="16,6 28,16 16,26 4,16" fill="none" stroke="#fbc02d" strokeWidth="1.5" />
    </svg>
  ), tooltip: 'Visual Universe: See chart for any day or animate the chart from 2 hours change to 1 week change every second. This is very useful for advanced studies and generating insights', action: 'open_visual_universe' },
  { component: (
    <svg width="28" height="28" viewBox="0 0 28 28">
      {/* Peaks with dots above them, counting back to a 0 anchor on the right. */}
      <polyline points="3,20 8,9 13,17 18,7 24,14" fill="none" stroke="#1b5e20" strokeWidth="1.8" />
      <circle cx="8" cy="5" r="1.9" fill="#1b5e20" /><circle cx="18" cy="3" r="1.9" fill="#1b5e20" />
      <text x="24" y="24" textAnchor="middle" fontSize="9" fontWeight="bold" fill="#1b5e20" fontFamily="sans-serif">0</text>
    </svg>
  ), tooltip: 'Pivot Addition Series (0-Series) — click a bar to set it as bar 0, then counts BACK from exactly that bar and dots the pivots that fall on an addition series (each term the sum of the previous two), within 1 bar. The same distances are then mirrored FORWARD as hollow dots on priced bars and dashed lines in the future space. Defaults to the last bar. Pivots need 5 higher/lower bars on each side.', action: 'run_pivot_addition_series', adminOnly: true },
  { component: (
    <svg width="28" height="28" viewBox="0 0 28 28">
      <polyline points="3,18 9,8 15,19 21,6 25,13" fill="none" stroke="#0d47a1" strokeWidth="1.8" />
      <circle cx="9" cy="4" r="1.9" fill="#0d47a1" /><circle cx="15" cy="23" r="1.9" fill="#e65100" />
      <text x="23" y="25" textAnchor="middle" fontSize="8" fontWeight="bold" fill="#0d47a1" fontFamily="sans-serif">.618</text>
    </svg>
  ), tooltip: 'Fibonacci Pivot Pairs — scans forward for pivot pairs 20-300 bars apart and dots the pivots landing on Fibonacci fractions and extensions of that span, within 1 bar. Pivots need 5 higher/lower bars on each side. Dashed PROJ lines mark ratios falling beyond the last bar — incomplete setups to watch.', action: 'run_fib_pivot_pairs', adminOnly: true },
  { component: (
    <svg width="28" height="28" viewBox="0 0 28 28">
      {/* Two anchors on the left, dashed projections running right into future space. */}
      <line x1="4" y1="4" x2="4" y2="24" stroke="#455a64" strokeWidth="2" />
      <line x1="10" y1="4" x2="10" y2="24" stroke="#455a64" strokeWidth="2" />
      <line x1="17" y1="5" x2="17" y2="23" stroke="#e65100" strokeWidth="1.8" strokeDasharray="3 2" />
      <line x1="24" y1="5" x2="24" y2="23" stroke="#e65100" strokeWidth="1.8" strokeDasharray="3 2" />
    </svg>
  ), tooltip: 'Recent Pivot Fib Projections — anchors to the two most recent 5-bar pivots and projects Fibonacci multiples of that interval forward. Dashed orange lines are future targets. Click again to remove.', action: 'toggle_recent_fib_projections', adminOnly: true },
  /*
  /*
   * ── THE THREE PANEL-NATIVE GEOMETRY TOOLS ARE NOT HERE, and that is the whole decision ─────
   *
   * SK360, Time Projection and SK-Grid are `PANEL_TOOLS`: they have never been toolbar actions,
   * and `adminPanelTools.js` asserts they are absent from `RELOCATED_ACTIONS` for that reason.
   *
   * <p>They carried `catalogOnly: true` entries here for one day, so the full catalog could claim
   * to show every capability. That claim lost to a simpler one on 2026-11-01: geometric projection
   * tools live in the Admin Panel's Price & Geometry tab and nowhere else, so a toolbar icon for
   * them — even one only an admin in catalog mode could see — is a second answer to where they
   * live. The `catalogOnly` mechanism went with them, having no remaining users.
   *
   * <p>Nothing was lost. All three are in that tab, with their own icons, labels and tooltips, and
   * their dispatcher cases are untouched.
   */
  { component: <AccessTimeIcon />, tooltip: 'Intraday Animation — animate the Vedic chart through time for any location and date to research intraday timeframes', action: 'open_intraday_animation', adminOnly: true },
  { component: <CompareArrowsIcon />, tooltip: 'Compare two time periods of the chart side by side or overlaid to visually identify pattern similarities', action: 'open_chart_comparison' },
  // Spacer after Visual Universe / Intraday icons
  { component: <span style={{ width: 36, display: 'inline-block' }} />, tooltip: '', action: undefined },
            // RSI toggle icon (zigzag/sine wave) - 17th from left, 5th from right
  { component: (
      <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <svg width="28" height="28" viewBox="0 0 28 28">
          <polyline points="2,20 7,10 12,18 17,8 22,16 26,12" fill="none" stroke="#1976d2" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
        </svg>
        <span style={{fontSize: 10, color: '#1976d2', fontWeight: 'bold', marginTop: -2}}>RSI</span>
      </span>
    ), tooltip: 'Toggle RSI overlay in the Volume Panel to combine this technical study with other astro-studies if desired', action: 'toggle_rsi_overlay' },
  // MACD toggle icon (histogram/signal lines)
  { component: (
      <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <svg width="28" height="28" viewBox="0 0 28 28">
          {/* MACD histogram bars */}
          <rect x="4" y="14" width="2" height="6" fill="#1976d2" />
          <rect x="8" y="10" width="2" height="10" fill="#1976d2" />
          <rect x="12" y="16" width="2" height="4" fill="#1976d2" />
          <rect x="16" y="12" width="2" height="8" fill="#1976d2" />
          <rect x="20" y="18" width="2" height="2" fill="#1976d2" />
          {/* MACD signal line */}
          <polyline points="4,20 8,16 12,18 16,14 20,18 24,16" fill="none" stroke="#d32f2f" strokeWidth="2" />
        </svg>
        <span style={{fontSize: 10, color: '#1976d2', fontWeight: 'bold', marginTop: -2}}>MACD</span>
      </span>
    ), tooltip: 'Toggle standard MACD overlay in the Volume Panel to combine this technical study with other astro-studies if desired', action: 'toggle_macd_overlay' },
  // Insert horizontal lines icon right after MACD
  { component: (
      <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        {/* Correct 3 horizontal parallel lines icon */}
        <svg width="28" height="28" viewBox="0 0 28 28">
          <rect x="4" y="7" width="20" height="3" rx="1.5" fill="#e53935" />
          <rect x="4" y="13" width="20" height="3" rx="1.5" fill="#fbc02d" />
          <rect x="4" y="19" width="20" height="3" rx="1.5" fill="#43a047" />
        </svg>
        <span style={{fontSize: 10, color: '#1976d2', fontWeight: 'bold', marginTop: -2}}>SR</span>
      </span>
    ), tooltip: 'Draw one or more horizontal lines at desired price levels to view the chart technically with your own support and resistance lines', action: 'open_horizontal_line_dialog' },
    { component: <DonutLargeIcon />, tooltip: 'Configure multiple moving averages and add those to or remove from the chart', action: 'open_pattern_config_dialog' },
  // Drawing tools
  { component: (
    <svg width="28" height="28" viewBox="0 0 28 28">
      {/* Endpoint dots: these tools now draw a bounded segment, not a full-height line. */}
      <line x1="14" y1="6" x2="14" y2="22" stroke="#1976d2" strokeWidth="2.5" strokeLinecap="round" />
      <circle cx="14" cy="6" r="2.6" fill="#1976d2" />
      <circle cx="14" cy="22" r="2.6" fill="#1976d2" />
    </svg>
  ), tooltip: 'Draw a vertical segment — click the start point, then click to set how far it extends. Only the second click’s price is used. Press Esc to cancel.', action: 'draw_vertical_line' },
  { component: (
    <svg width="28" height="28" viewBox="0 0 28 28">
      {/* The dashed tail that used to be here implied the line ran off the chart; it no longer does. */}
      <line x1="6" y1="14" x2="22" y2="14" stroke="#e53935" strokeWidth="2.5" strokeLinecap="round" />
      <circle cx="6" cy="14" r="2.6" fill="#e53935" />
      <circle cx="22" cy="14" r="2.6" fill="#e53935" />
    </svg>
  ), tooltip: 'Draw a horizontal segment — click the start point, then click to set how far it extends. Only the second click’s date is used. Press Esc to cancel.', action: 'draw_horizontal_line' },
  { component: (
    <svg width="28" height="28" viewBox="0 0 28 28">
      <line x1="4" y1="24" x2="24" y2="4" stroke="#388e3c" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  ), tooltip: 'Draw a trendline — click two points to connect them. Press Esc to cancel.', action: 'draw_trendline' },
  // Rendered by the row below rather than as a static icon, for the same reason as the text tool:
  // the glyph has to show which of the two states the NEXT line will be drawn in, and static
  // markup cannot. It sits beside the trendline tool because that is what it modifies, and it does
  // not activate drawing — pressing it only changes what the next drawn line carries.
  { component: (
    <svg width="28" height="28" viewBox="0 0 28 28">
      <rect x="5" y="7" width="18" height="14" rx="1.5" fill="none" stroke="#ff9800" strokeWidth="2" />
    </svg>
  ), tooltip: 'Draw a rectangle — click two diagonal corners. Press Esc to cancel.', action: 'draw_rectangle' },
  // Rendered by the row below rather than as a static icon: the glyph grows and the badge changes
  // with the size the next click will apply, which static markup cannot show.
  { isTextTool: true, action: 'draw_text' },
  { component: (
    <svg width="28" height="28" viewBox="0 0 28 28">
      {/* Spans the full height of the icon, as the tool spans the full height of the pane. */}
      <line x1="14" y1="2" x2="14" y2="26" stroke="#00838f" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  ), tooltip: 'Full vertical line — one click draws a line spanning the whole chart height at that bar. Carries no move measurement. Press Esc to cancel.', action: 'draw_vertical_full' },
  { component: (
    <svg width="28" height="28" viewBox="0 0 28 28">
      <circle cx="14" cy="14" r="8" fill="none" stroke="#5e35b1" strokeWidth="2.2" />
    </svg>
  ), tooltip: 'Empty circle — click to place an outlined circle. Click the tool again to cycle Small / Medium / Large. Press Esc to cancel.', action: 'draw_circle_empty' },
  { component: (
    <svg width="28" height="28" viewBox="0 0 28 28">
      <circle cx="14" cy="14" r="8" fill="#5e35b1" />
    </svg>
  ), tooltip: 'Filled circle — click to place a solid circle. Click the tool again to cycle Small / Medium / Large. Press Esc to cancel.', action: 'draw_circle_filled' },

  { component: (
    <svg width="28" height="28" viewBox="0 0 28 28">
      {/* Evenly repeating verticals, the anchor solid in the middle. */}
      <line x1="4"  y1="4" x2="4"  y2="24" stroke="#8e24aa" strokeWidth="1.6" strokeDasharray="3 2" />
      <line x1="11" y1="4" x2="11" y2="24" stroke="#8e24aa" strokeWidth="1.6" strokeDasharray="3 2" />
      <line x1="18" y1="2" x2="18" y2="26" stroke="#8e24aa" strokeWidth="2.4" />
      <line x1="25" y1="4" x2="25" y2="24" stroke="#8e24aa" strokeWidth="1.6" strokeDasharray="3 2" />
    </svg>
  ), tooltip: 'Fixed Length Cycles — enter a cycle length in bars, then click a bar to anchor it. Draws repeating verticals every N bars both backwards through history and forwards into future space.', action: 'open_fixed_cycle_dialog' },
  { component: (
    <svg width="28" height="28" viewBox="0 0 28 28">
      {/* A clock face with dashed verticals marching right: time, projected forward. */}
      <circle cx="8" cy="9" r="5.5" fill="none" stroke="#7b1fa2" strokeWidth="1.8" />
      <line x1="8" y1="9" x2="8" y2="5.5" stroke="#7b1fa2" strokeWidth="1.6" strokeLinecap="round" />
      <line x1="8" y1="9" x2="10.5" y2="10.5" stroke="#7b1fa2" strokeWidth="1.6" strokeLinecap="round" />
      <line x1="16" y1="3" x2="16" y2="25" stroke="#7b1fa2" strokeWidth="1.6" strokeDasharray="3 2" />
      <line x1="21" y1="3" x2="21" y2="25" stroke="#7b1fa2" strokeWidth="1.6" strokeDasharray="3 2" />
      <line x1="25" y1="3" x2="25" y2="25" stroke="#7b1fa2" strokeWidth="1.6" strokeDasharray="3 2" />
    </svg>
  ), tooltip: 'Fibonacci Time — click two pivots. Projects Fibonacci multiples of the interval between them forward in time from the second point, into future space. Press Esc to cancel.', action: 'draw_fib_time' },
  { component: (
    <svg width="28" height="28" viewBox="0 0 28 28">
      <line x1="4" y1="6"  x2="24" y2="6"  stroke="#0288d1" strokeWidth="2" strokeDasharray="3 2" />
      <line x1="4" y1="12" x2="24" y2="12" stroke="#0288d1" strokeWidth="2" strokeDasharray="3 2" />
      <line x1="4" y1="18" x2="24" y2="18" stroke="#0288d1" strokeWidth="2" strokeDasharray="3 2" />
      <line x1="4" y1="22" x2="24" y2="4"  stroke="#01579b" strokeWidth="2" />
    </svg>
  ), tooltip: 'Fibonacci retracement — click the start and end of a move. Draws 23.6/38.2/50/61.8/76.4% levels. Press Esc to cancel.', action: 'draw_fibonacci' },
  { component: (
    <svg width="28" height="28" viewBox="0 0 28 28">
      <line x1="4" y1="22" x2="15" y2="11" stroke="#6a1b9a" strokeWidth="2.5" strokeLinecap="round" />
      <line x1="15" y1="11" x2="25" y2="1" stroke="#6a1b9a" strokeWidth="2" strokeDasharray="3 2" />
      <circle cx="4" cy="22" r="2.2" fill="#6a1b9a" /><circle cx="15" cy="11" r="2.2" fill="#6a1b9a" />
    </svg>
  ), tooltip: 'Extended trendline — click two points to set the slope, then a third to set how far it projects. Press Esc to cancel.', action: 'draw_extended_trendline' },
  { component: (
    <svg width="28" height="28" viewBox="0 0 28 28">
      <line x1="3" y1="14" x2="11" y2="14" stroke="#2e7d32" strokeWidth="2" />
      <line x1="11" y1="6"  x2="11" y2="22" stroke="#2e7d32" strokeWidth="2" />
      <line x1="11" y1="14" x2="25" y2="14" stroke="#1b5e20" strokeWidth="2.5" />
      <line x1="11" y1="6"  x2="25" y2="6"  stroke="#2e7d32" strokeWidth="2" strokeDasharray="3 2" />
      <line x1="11" y1="22" x2="25" y2="22" stroke="#2e7d32" strokeWidth="2" strokeDasharray="3 2" />
    </svg>
  ), tooltip: 'Andrews’ pitchfork — click three pivots. Draws the median from the first through the midpoint of the other two, with parallel prongs. Press Esc to cancel.', action: 'draw_pitchfork' },
  // Colour selector for the five drawing tools above. A marker rather than a component because
  // ICONS is a module-level constant and this control needs state; the row renderer swaps it in,
  // which keeps the toolbar's left-to-right order defined in one place.
  { isDrawingColorPicker: true },
{ component: <span style={{ width: 36, display: 'inline-block' }} />, tooltip: '', action: undefined },
  /*
    CONFIGURATION and MESSAGES, and they are LAST on purpose.

    The short-bar preset hides two runs: the leading SHORT_BAR_HIDE_LEADING icons, and
    SHORT_BAR_HIDE_FROM_FLASK from the 'Strategy 1' flask onward. That second run ends at 'Compare
    two time periods', well before here - so appending is what puts these two on the short bar,
    which is the preset a trial customer is looking at. Inserting them anywhere earlier would either
    land them inside a hidden run or shift what those runs cover.

    NOT adminOnly. Every study button near them is, because a study under evaluation should not
    reach a customer; these are the customer's own settings, and gating them would hide the feature
    from the only people it is for.
  */
  { component: (
    /*
      A gear with a palette inside it: "configure" and "colour" in one glyph, which is what this
      dialog actually does. A bare gear would read as application settings and a bare palette as a
      drawing colour - the toolbar already has one of those, three icons to the left.
    */
    <svg width="28" height="28" viewBox="0 0 28 28" aria-label="Configuration" focusable="false">
      <path d="M14 3.2 l1.6 0 l.5 2.2 a8.8 8.8 0 0 1 2.1 .9 l1.8 -1.4 l1.1 1.1 l-1.4 1.8
               a8.8 8.8 0 0 1 .9 2.1 l2.2 .5 l0 1.6 l-2.2 .5 a8.8 8.8 0 0 1 -.9 2.1 l1.4 1.8
               l-1.1 1.1 l-1.8 -1.4 a8.8 8.8 0 0 1 -2.1 .9 l-.5 2.2 l-1.6 0 l-.5 -2.2
               a8.8 8.8 0 0 1 -2.1 -.9 l-1.8 1.4 l-1.1 -1.1 l1.4 -1.8 a8.8 8.8 0 0 1 -.9 -2.1
               l-2.2 -.5 l0 -1.6 l2.2 -.5 a8.8 8.8 0 0 1 .9 -2.1 l-1.4 -1.8 l1.1 -1.1 l1.8 1.4
               a8.8 8.8 0 0 1 2.1 -.9 z"
        fill="none" stroke="#455a64" strokeWidth="1.5" strokeLinejoin="round" />
      <circle cx="11.6" cy="12.4" r="1.5" fill="#e53935" />
      <circle cx="16.4" cy="12.4" r="1.5" fill="#1e88e5" />
      <circle cx="14" cy="16.4" r="1.5" fill="#43a047" />
    </svg>
  ), alt: 'Configuration', tooltip: 'Configuration — choose your own colours for the PTA and PTB bands and the CTS arrows. Saved in this browser and applied to the chart immediately.', action: 'open_configuration_dialog' },
  { component: (
    /*
      An envelope with an unread dot. The dot is what makes it a MESSAGES icon rather than a
      "contact us" one, and it is drawn unconditionally for now - there is no message state to read
      yet, and a dot that never appears would be indistinguishable from a glyph that has none.
    */
    <svg width="28" height="28" viewBox="0 0 28 28" aria-label="Messages" focusable="false">
      <rect x="3.5" y="7" width="21" height="14" rx="2"
        fill="none" stroke="#455a64" strokeWidth="1.5" />
      <path d="M4.5 8.5 L14 15.5 L23.5 8.5" fill="none" stroke="#455a64" strokeWidth="1.5"
        strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="22.5" cy="7.5" r="3.6" fill="#e53935" />
    </svg>
  ), alt: 'Messages', tooltip: 'Messages', action: 'open_messages_dialog' },

  /*
    THE DYNAMAN SIMULATOR. adminOnly, because a simulation is an unbounded read across a symbol's
    whole history and a stepped run holds a server thread while somebody watches. The endpoints
    require authentication on their own authority too - this flag stops a control being shown that
    would fail, it is not the permission.
  */
  // `component`, NOT `icon`. The renderer reads `icon.component`; an entry keyed `icon` supplies
  // undefined, React renders nothing, and the button collapses to an 8px blank gap in the
  // toolbar — present, admin-gated correctly, fully clickable, and invisible. It shipped that
  // way to production and was only found by someone looking for a button they could not see.
  // toolbarIcons.test.js now asserts every entry is renderable.
  // FINDABLE, not merely rendered.
  //
  // This drew correctly the whole time — computed paint was stroke rgb(69,90,100), fill
  // rgb(142,36,170), opacity 1, visibility visible, and a hit test on its centre landed on its
  // own path. It was still the hardest glyph on the bar to spot, and two of us failed to: it was
  // 28px with 1.5px strokes among neighbours at 30-36px and 2-2.2px, and it was the ONLY inline
  // SVG in this file hardcoding a colour instead of inheriting.
  //
  // `currentColor` is what the rest use. IconButton carries color="primary", so the glyph picks
  // up the same blue as every sibling and tracks the theme instead of drifting from it. The
  // purple dot stays — it is the one thing that distinguishes this icon from the other charts on
  // the bar, and the whole point of a distinguishing mark is that it does not match.
  /*
    ALPHA REPLAY — replay the chart with the future hidden. Relocated here 2026-10-20, from the
    end of the study cluster, to sit DIRECTLY BEFORE the DynaMan Simulator.

    The three icons from here to the Workbench now read as one group: this one hands a reader the
    chart bar by bar, the next configures and measures a strategy, the third pits the two against
    each other. Up in the study cluster it was the only entry that drew nothing.

    A play head — a triangle against a bar — rather than a plain play arrow, because the control
    is not "play the chart", it is "put the edge of the known world here".

    ⚠ 30px, not the 28 it carried in the cluster. Its new neighbours are 30px on the same 28-unit
    viewBox, and this file already records what a 28px glyph among 30px ones costs: the DynaMan
    icon below drew correctly the whole time, was measured as visible, and was still the hardest
    mark on the bar to find — by exactly this mismatch. Same glyph, same proportions, matched to
    the row it now sits in.

    The hardcoded #00695c is 2.6:1 on the dark toolbar and needs no entry here: `legibleIcon`
    lifts any glyph under 3:1 and returns one that already reads by identity, so this is handled
    at render rather than by a second hex in the source.
  */
  { component: (
    <svg width="30" height="30" viewBox="0 0 28 28" aria-label="Alpha Replay" focusable="false">
      <path d="M6 6 L16 14 L6 22 Z" fill="#00695c" />
      <line x1="21" y1="4" x2="21" y2="24" stroke="#00695c" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  // The tooltip names the CTS stop because the click no longer always does what the old wording
  // promised, and a control that describes behaviour it does not have is the stale-copy defect
  // this file keeps paying for. Arrow keys are named in the same breath for the same reason: they
  // are now the only gesture that is guaranteed to move exactly one bar.
  ), alt: 'Alpha Replay', tooltip: 'Alpha Replay - replay the chart with the future hidden. Click a hidden bar to reveal up to it, or anywhere on the revealed side to advance; with a Critical Timing Scan on the chart a click stops at the next CTS point instead. Arrow keys always step one bar either way.', action: 'toggle_simulate' },
  // ⚠ NO `adminOnly`. DynaMan is open to every role and plan — trial and paying, astro and
  // integralDelta — so the flag is gone rather than being lifted per product. The
  // `CUSTOMER_FACING_BY_PRODUCT` exemption that used to carry it on integralDelta went with it:
  // a flag plus an exemption cancelling it out is two answers to one question, which is the
  // arrangement `nonAdminChrome.js` warns about in its own docblock.
  { component: (
    <svg width="30" height="30" viewBox="0 0 28 28" aria-label="DynaMan Simulator" focusable="false">
      <rect x="3" y="4" width="22" height="20" rx="2"
        fill="none" stroke="currentColor" strokeWidth="2" />
      <path d="M6.5 18 L10.5 12.5 L14 15.5 L18 8.5 L21.5 13"
        fill="none" stroke="currentColor" strokeWidth="2"
        strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="18" cy="8.5" r="2.6" fill="#8e24aa" />
    </svg>
  // ⚠ "(admin)" DROPPED FROM THE TOOLTIP, and now it is simply true rather than true-per-product.
  // A hover telling a customer the control they are about to click is for administrators is worse
  // than no hover at all.
  ), alt: 'DynaMan Simulator', tooltip: 'DynaMan Simulator',
    action: 'open_dynaman_simulator' },
  // THE FLIGHT SIMULATOR. Its own icon beside the research dialog rather than a tab inside it,
  // because the two answer different questions: that one configures and measures a strategy, this
  // one hands a reader the chart bar by bar and asks whether they would have done better.
  //
  // A PLAY TRIANGLE INSIDE A FRAME, which is the one shape on this bar that says "watch" rather
  // than "compute". The currentColor stroke tracks the theme like every sibling; the amber
  // triangle is the distinguishing mark, and the point of a distinguishing mark is that it does
  // not match.
  //
  // ⚠ NO `adminOnly`, and the sentence that used to be here is worth keeping as a caveat rather
  // than a gate: a session IS an unbounded read across a symbol's whole history, and a stepped one
  // holds one of two server-side stream slots while the tab is open. The endpoint is still the
  // permission — it now requires a signed-in user rather than an administrator — and the cost is
  // bounded by that cap rather than by who may click this.
  { component: (
    <svg width="30" height="30" viewBox="0 0 28 28" aria-label="Launch Workbench" focusable="false">
      <rect x="3" y="4" width="22" height="20" rx="2"
        fill="none" stroke="currentColor" strokeWidth="2" />
      <path d="M11.5 9.5 L19 14 L11.5 18.5 Z" fill="#ffb300" />
      <path d="M6.5 9.5 L6.5 18.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  ), alt: 'Launch Workbench',
  tooltip: 'Launch the Verifiable Alpha Workbench — replay the chart bar by bar, trading '
    + 'against the engine with the future hidden',
    action: 'launch_simulator' },
  // CHART ANALYTICA. A magnifier over a bar chart, because what it does is INSPECT what is
  // already drawn rather than compute a new study — it reads the bands, the CTS marks, the RSI
  // and the pivots the chart is showing and tells you what they did.
  //
  // The toolbar is the SECOND way in; right-clicking a bar is the first, and is the one that
  // carries a bar with it. This button scans to the LAST bar, which is the question a reader
  // asks when they have not got a particular bar in mind.
  //
  // The amber lens is the distinguishing mark; the currentColor bars track the theme like every
  // sibling. Not admin-gated: it reads the chart in front of the reader and calls no endpoint.
  { component: (
    <svg width="30" height="30" viewBox="0 0 28 28" aria-label="Chart Analytica" focusable="false">
      <rect x="3" y="4" width="22" height="20" rx="2"
        fill="none" stroke="currentColor" strokeWidth="2" />
      <rect x="6.5" y="15" width="2.5" height="5" fill="currentColor" />
      <rect x="10.5" y="12" width="2.5" height="8" fill="currentColor" />
      <rect x="14.5" y="16" width="2.5" height="4" fill="currentColor" />
      <circle cx="18.5" cy="11" r="4" fill="none" stroke="#ffb300" strokeWidth="2" />
      <path d="M21.4 13.9 L24 16.5" stroke="#ffb300" strokeWidth="2" strokeLinecap="round" />
    </svg>
  ), alt: 'Chart Analytica',
  tooltip: 'Chart Analytica — scan every bar from the oldest up to the last one and report the '
    + 'PTA/PTB, CTS, RSI, volume and Fibonacci statistics. Right-click any bar to scan to that '
    + 'bar instead.',
    action: 'chart_analytica' },
  /*
   * THE MARKET SCANNER, on the toolbar as of now — it was reachable only from the Admin Panel.
   *
   * Beside Chart Analytica because they are the two scanning tools and the pair reads as a group:
   * that one scans every BAR of one symbol, this one scans every SYMBOL of one market. Same verb,
   * orthogonal axis.
   *
   * ⚠ `adminOnly: true` AND listed in `CUSTOMER_FACING_BY_PRODUCT[integraldelta]`, which together
   * mirror the BACKEND's rule exactly — `ProductAudience.allowsIntegralDeltaFeature()` is admin OR
   * integralDelta. The two gates have to agree or one of them is a lie: `adminOnly` alone would
   * hide it from the customers it is for, and omitting `adminOnly` would show it to ASTRO
   * customers, whom the server refuses with 403. That second mistake is exactly what shipped with
   * the cycle pills a few commits ago, so it is spelled out rather than left to be inferred.
   *
   * No market or date picker, unlike the Admin Panel's version: a toolbar icon has nowhere to put
   * one. It runs the DEPLOYMENT's market with no date, which is the panel's own "latest session"
   * default, and the results dialog it opens names the market it scanned — so the one thing a
   * reader could get wrong about a parameterless button is answered on screen rather than assumed.
   */
  { component: (
    <svg width="30" height="30" viewBox="0 0 28 28" aria-label="Market Scanner" focusable="false">
      {/* A grid of symbols with two of them struck — "every symbol, a few match". */}
      <rect x="3" y="4" width="22" height="20" rx="2"
        fill="none" stroke="currentColor" strokeWidth="2" />
      <path d="M7 9 H21 M7 13 H21 M7 17 H21 M7 21 H15" stroke="currentColor"
        strokeWidth="1.6" strokeLinecap="round" />
      <circle cx="19.5" cy="17" r="4.2" fill="none" stroke="#10B981" strokeWidth="2.2" />
      <path d="M17.7 17 L19.2 18.5 L21.5 15.6" stroke="#10B981" strokeWidth="2"
        strokeLinecap="round" strokeLinejoin="round" fill="none" />
    </svg>
  ), alt: 'Market Scanner',
  tooltip: 'Market Scanner — check every symbol in the market against the four top and four '
    + 'bottom lines of both PTA and PTB, within 0.25%, and list what is near one.',
    // ⚠ BOTH ON ONE LINE, and that is a convention with teeth rather than formatting. Every
    // source-reading test here slices an entry from `{ component:` to the END OF THE LINE
    // carrying `action:` — so `adminOnly` on the next line is invisible to all of them. Written
    // that way first, and `entryFlagsFor` reported the flag as false while the object plainly
    // had it: the gate worked and every test about it was vacuous.
    action: 'run_market_scanner', adminOnly: true },
  // THE PORTFOLIO MANAGER. Its own icon rather than a tab inside the simulator: the simulator
  // answers "what did this strategy do" and this answers "what does the BOOK do when several of
  // them compete for one pot of capital". Different question, different surface.
  //
  // Stacked bars with a filled slice, because the thing being configured is an allocation. The
  // currentColor stroke tracks the theme like every sibling; the amber slice is the one
  // distinguishing mark, and the point of a distinguishing mark is that it does not match.
  { adminOnly: true, component: (
    <svg width="30" height="30" viewBox="0 0 28 28" aria-label="Portfolio Manager" focusable="false">
      <rect x="3" y="4" width="22" height="20" rx="2"
        fill="none" stroke="currentColor" strokeWidth="2" />
      <rect x="7" y="14" width="3.5" height="6" fill="currentColor" />
      <rect x="12.25" y="10" width="3.5" height="10" fill="#ffb300" />
      <rect x="17.5" y="12" width="3.5" height="8" fill="currentColor" />
    </svg>
  ), alt: 'Portfolio Manager', tooltip: 'Portfolio Manager (admin)',
    action: 'open_portfolio_manager' },
      // Chart style toggle icon is injected dynamically as the last icon
  /**
   * THE ADMIN PANEL, at the end of the toolbar where a settings control belongs.
   *
   * `adminOnly` hides it for everyone else; the dialog checks again for itself. Two gates,
   * because they fail differently -- an icon can be hidden while a stale `open` still reaches the
   * dialog.
   */
  { component: (
    <svg width="26" height="26" viewBox="0 0 24 24" aria-label="Admin Panel" focusable="false">
      <path d="M12 2.5 L19.5 5.5 V11 c0 4.6 -3.1 8.3 -7.5 9.5 C7.6 19.3 4.5 15.6 4.5 11 V5.5 Z"
        fill="none" stroke="#37474f" strokeWidth="1.6" strokeLinejoin="round" />
      <circle cx="12" cy="10.5" r="2.1" fill="none" stroke="#37474f" strokeWidth="1.5" />
      <path d="M12 12.6 v3.2" stroke="#37474f" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  ), tooltip: 'Admin Panel — the diagnostic and research tools, moved off the main toolbar (admin)',
    action: ADMIN_PANEL_ACTION, adminOnly: true },

];

const TabPanel = (props) => {
  const { clearOverlays, overlays } = useChartOverlays();

  /**
   * WHICH PRODUCT THIS BUNDLE IS SERVED AS — the third visibility axis.
   *
   * <p>⚠ RESOLVED HERE RATHER THAN PASSED AS A PROP, deliberately. It is a property of the
   * DEPLOYMENT, not of this component's caller: a prop would be one more thing App has to thread
   * and one more place it can be forgotten, and a forgotten prop would read as `undefined` —
   * which `isActionVisibleForProduct` treats as ASC, silently showing the icons this gate exists
   * to hide. Resolving it at the point of use cannot be forgotten.
   *
   * <p>Not memoised: a hostname cannot change inside a session, so the value is constant, and
   * `resolveHostContext` is a substring test over a short string. A `useMemo` would cost a
   * dependency array to get wrong.
   */
  /*
   * THE BUILD TARGET, not hostContext's two-valued answer: that one collapses `admin` onto ID,
   * which would hand an internal build integralDelta's restrictions. `ACTIVE_PRODUCT` is one of
   * the three and `PRODUCT_DENIES` keys on exactly those.
   *
   * ⚠ A PROP NOW, FALLING BACK TO THE BAKED VALUE. The Admin Panel can point an administrator at
   * another product to inspect it, and the toolbar has to re-filter when they do — reading the
   * module constant here made that unreachable by construction. The fallback is what keeps every
   * other caller, and every test that renders this without the prop, on the build's own target:
   * the override is an admin inspection tool and the baked value is still the only thing that
   * ships. `App` owns the state and does the role test; see `readProductOverride`.
   */
  const product = props.product || ACTIVE_PRODUCT;
  /*
   * THE DRAWN ICONS, LIFTED so they read on whichever bar they are on.
   *
   * <p>Many carry hardcoded Material 800/900 hues chosen against a white toolbar — measured on
   * the dark bar, fourteen of the thirty-three distinct colours up there were under 3:1, which is
   * a toolbar a reader cannot use. `legibleIcon` raises only the LIGHTNESS, only of the ones that
   * fail, and leaves a glyph on a filled plate measured against its plate. See that module.
   *
   * <p>On the LIGHT bar this is a no-op by construction — every one of those hues was picked to
   * read on white — so the light toolbar is byte-identical to what it was.
   *
   * <p>Memoised on the bar colour alone. The ICONS array is module-scope and never changes, so
   * without this the whole tree would be cloned on every render of a component that re-renders on
   * every overlay change.
   */
  const theme = useTheme();
  const toolbarBg = theme.palette.background.paper;
  const legible = React.useMemo(() => {
    const cache = new Map();
    return (node) => {
      if (!cache.has(node)) cache.set(node, legibleIcon(node, toolbarBg));
      return cache.get(node);
    };
  }, [toolbarBg]);
  const hasAstroGraph = (overlays || []).some(o => o.type === 'astroGraphLine');
  // Drives the Delete Last button's enabled state and tooltip. Counts undoable UNITS, not
  // overlays: one study run and one Fibonacci retracement are two units, however many lines each
  // put on the chart.
  const drawingCount = countOverlayGroups(overlays);
  const nextUndoSize = lastOverlaySize(overlays);
  // State for VedicAspectsStudyDialog
  const [vedicAspectsOpen, setVedicAspectsOpen] = React.useState(false);
  console.log('[TabPanel] props:', props);
  /*
   * TWENTY-TWO DESTRUCTURED CALLBACKS USED TO SIT HERE and are gone with the switch they fed.
   *
   * Every one of them — onToggleRSI, onOpenControlsDialog, onOpenStrategyDesignDialog and the
   * rest — existed only to be named inside `handleIconClick`. The router reads them off the
   * spread props object now, so destructuring them again would be a second name for the same
   * value with nothing reading it: eslint flagged all twenty-two the moment the switch left.
   *
   * What remains below is the set this component uses in its own RENDER, which is a different
   * question and is why they were not removed together.
   */
  const {
    chartType,
    onToggleChartType,
    onOpenCyclesDialog,
    onTriggerAreaZoom,
  } = props;
  const [adminPanelOpen, setAdminPanelOpen] = useState(false);

  /**
   * THE ONE ENTRY POINT FOR EVERY TOOLBAR ACTION — now a thin adapter over a shared router.
   *
   * <p>The 47-case switch moved to {@code utils/actionDispatcher.js} verbatim. It lived here as a
   * local const, so the only way to reach any of these capabilities was a DOM click; the
   * architecture audit named that the blocker for a Command Bus. Nothing about the routing
   * changed — this function now assembles the handler map and delegates.
   *
   * <p>`...props` is spread so all 47 callbacks map BY CONSTRUCTION rather than through a
   * hand-written list that would go stale on the next prop added. The four entries after it
   * cannot come from props: two are this component's own state, one is the overlay context, and
   * {@code drawingMode} is a value the drawing chain reads rather than a callback.
   *
   * <p>Built per CLICK rather than memoised, deliberately. `handleIconClick` was never memoised
   * either, so this is the same cost it has always had — and a memo here would need every
   * callback prop in its dependency array, where one omission is a handler frozen at the value it
   * had on first render.
   */
  const handleIconClick = (action) => {
    dispatchToolbarAction(action, {
      ...props,
      onToggleAdminPanel: () => setAdminPanelOpen((prev) => !prev),
      clearOverlays,
      // App owns `cycleRuns`; the overlay store does not. Forwarded so clear-all can purge both.
      onClearCycleRuns: props.onClearCycleRuns,
      onOpenMoaFallback: () => setMoaStrategyDialogOpen(true),
    });
  };

  // ── THE 47-CASE SWITCH THAT USED TO BE HERE ───────────────────────────────────────────
  //
  // Moved WHOLE to utils/actionDispatcher.js on 2026-10-24, including the three pre-switch
  // branches (the admin panel, the horizontal-line dialog, clear-all-overlays). Leaving those
  // three behind would have made it a half extraction: a caller using the router directly
  // would route 47 actions and silently drop three.
  //
  // DELETED rather than kept beside the new call. A second copy of the routing is not a safety
  // net, it is two answers to "what does this action do" that agree on the day they are written
  // and drift from the first edit onward -- and the dead one would still lint, still compile,
  // and still be the one somebody reads.
  //
  // TabPanel.routing.test.js follows the switch: it parses the dispatcher source now. That is
  // an anchor move, not a change of claim -- every assertion it makes is unchanged.



  const [planetNumberDialogOpen, setPlanetNumberDialogOpen] = React.useState(false);
  const [moaStrategyDialogOpen, setMoaStrategyDialogOpen] = React.useState(false);
  const [barFloaterDialogOpen, setBarFloaterDialogOpen] = React.useState(false);


  // Recomputed whenever the mode changes, which is what makes the bar re-render without a reload.
  const hiddenIcons = hiddenIconIndices(ICONS, props.barMode);
  /*
   * Resolved ONCE per render, like `product` above it. Every bypass in this component reads
   * this one boolean, so the mode cannot be half-applied -- a toolbar showing the relocated
   * tools but still hiding the soft-deleted ones would be a catalog that is silently partial,
   * which is the failure a catalog can least afford.
   */
  const fullCatalog = showsFullCatalog(product, props.isAdmin);

  /*
   * THE SAME DECISION FOR THE CONTROLS THAT ARE NOT IN `ICONS`.
   *
   * Five are bare JSX further down -- the astro catalog, two TrendPulse dialogs, and the two
   * SOFT-DELETED ones (Auto-Analysis, Run Global Scan), which is why neither appeared in the
   * icon-array audit. They each asked `isActionVisibleForProfile` directly, so without this they
   * would stay hidden while the catalog claimed to be complete: a partial inventory that looks
   * total is worse than an obviously restricted one.
   */
  /**
   * THE MANIFEST'S ALLOW-LIST, as the set of ACTION ids it admits.
   *
   * <h2>⚠ THIS IS THE ADDITIVE HALF, AND IT REPLACES FOUR SUBTRACTIVE GATES</h2>
   *
   * `PROFILE_DENIES`, `PRODUCT_DENIES`, relocation-with-an-exemption-table and the bar-mode
   * preset each answered "does this icon render", in four places, and a catalog mode selectively
   * bypassed three of them. A product therefore gained a control by nobody naming it. Here a
   * manifest NAMES what it carries and an action it does not name is never mounted — so the
   * question has one answer, in one file, per product.
   *
   * <p>⚠ THE ORDER IS STILL THE DECLARATION'S, not the manifest's. `ICONS` is filtered rather
   * than re-sequenced, so nothing on screen MOVES as part of this refactor — a manifest list is
   * a SET for now. Making it authoritative for order needs the bare-JSX tail below to be
   * addressable too, which is a later stage; a half-applied ordering would interleave a manifest
   * order with a declaration order and read as a shuffled bar.
   */
  const manifest = useManifest();
  const allowedActions = React.useMemo(
    () => new Set(actionIdsOf(manifest.toolbar)),
    [manifest],
  );

  /**
   * Whether a tool reaches the toolbar.
   *
   * <p>Read by the ICONS loop AND by every bare-JSX control below, so the two cannot disagree —
   * which they could when the loop consulted four gates and the tail consulted one of them.
   */
  const showsAction = React.useCallback(
    (action) => allowedActions.has(action),
    [allowedActions],
  );

  /**
   * Whether a tool reaches the toolbar, BY TOOL ID.
   *
   * <h2>⚠ TWO PREDICATES BECAUSE THERE ARE TWO VOCABULARIES, and conflating them SHIPPED A BUG</h2>
   *
   * A tool's `id` and its `actionId` are deliberately different — `clear_all` dispatches
   * `clear_all_overlays`, `undo_last` dispatches `delete_last_drawing`, `chart_type` dispatches
   * `toggle_chart_type`. The ICONS loop only has `icon.action`, so it must ask by ACTION; the
   * bare-JSX controls below are hand-written and a reader naturally writes the TOOL id, which is
   * also what the manifests contain.
   *
   * <p>The first version of this had one action-keyed predicate and the bare-JSX gates passed
   * tool ids to it. Four of the eight were therefore ALWAYS FALSE — the X button, the chart-type
   * toggle, X1 and the cycles dialog would never have rendered, with no error anywhere. Caught by
   * `manifests.test.js`'s renderability check on its first run, which is the whole reason that
   * test exists: an allow-list fails by omission, and omission is silent.
   */
  const showsTool = React.useCallback(
    (toolId) => manifest.toolbar.includes(toolId),
    [manifest],
  );

  /*
   * ⚠ KEPT UNDER ITS OLD NAME so the existing call sites read unchanged, and still ACTION-keyed:
   * its three callers pass dispatcher action ids (`open_astro_catalog_notes` and the two
   * TrendPulse dialogs), not tool ids.
   */
  const showsControl = showsAction;

  return (
    <>
      {/* In catalog mode the bar WRAPS instead of scrolling: ~80 icons on one scrolling
          line means the reader cannot see the set they are choosing from, and the icons
          past the fold are the newest ones. Ordinary mode keeps its single row. */}
      <Box className="toolbar-gradient-bg" sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-start', alignItems: 'center', p: 0.5, overflowX: 'auto', ...(fullCatalog ? CATALOG_LAYOUT : {}) }}>
        {/* Resolved ONCE per render rather than per icon: `resolveHostContext` is deliberately
            uncached (a module-level cache would make it untestable), so calling it inside the
            map would repeat a substring test thirty times for an answer that cannot change
            inside a session. */}
        {ICONS.map((icon, idx) => {
          if (icon.isFlexSpacer) return <Box key={idx} sx={{ flex: 1 }} />;
          // THE ROLE GATE. `adminOnly` at the declaration is still the main mechanism; what this
          // adds is the six-action block a customer does not get and the one action they now do,
          // declared together in nonAdminChrome.js so "what does a customer see" is one list
          // rather than a flag on each of seven scattered entries.
          if (!isIconVisibleForRole(icon, props.isAdmin, product)) return null;
          /*
           * ⚠ THE FULL CATALOG, for astro + admin — see config/astroCatalog.js.
           *
           * It now suspends the PRODUCT rule and the bar-mode preset, and nothing else. It used to
           * suspend RELOCATION as well, which is what put the engine runners, the Portfolio
           * Manager and the utility cluster back on this bar; those tools have Admin Panel homes
           * now, so showing them here too would be two answers to where they live.
           *
           * The PROFILE deny list is applied either way, and that is the important asymmetry: the
           * profile dropdown is how an admin previews an ASC or AI toolbar, which is the review
           * this catalog exists to serve. Bypassing it would show everything under every profile
           * and destroy the instrument the next decision depends on.
           */
          /*
           * ⚠ THE MANIFEST GATE — the one that replaced four.
           *
           * `PROFILE_DENIES`, `PRODUCT_DENIES`, relocation and the bar-mode preset all used to
           * be consulted here, with a catalog mode bypassing three of them. An action the
           * resolved manifest does not name is simply not mounted, which is also how the six
           * unbuilt placeholder buttons leave the bar: they carry no action id, so no manifest
           * can name them.
           */
          if (!showsAction(icon.action)) return null;
          /*
           * ⚠ THE BAR-MODE PRESET IS STILL APPLIED, and it is the one subtractive rule that
           * survives. It is not a product definition — it is a reader's own layout preference,
           * by POSITION rather than by action, and it exists so `key={idx}` below still
           * addresses the entry it always did. Collapsing a preference into a product's tool
           * list would make it unexpressible.
           */
          if (!fullCatalog && hiddenIcons.has(idx)) return null;
          if (icon.isTextTool) {
            // The badge advertises what the NEXT click gives — the tool cycles Small, Medium,
            // Large on each activation — so someone can pick a size before committing to a click.
            const size = props.nextTextSize || { short: 'S', label: 'Small', px: 20 };
            const isActive = props.drawingMode === 'draw_text';
            return (
              <Tooltip
                arrow
                key={idx}
                placement="bottom"
                title={`${textToolTooltip(size)} — click to place, double-click to edit, drag to move. Clicking the tool again cycles the size. Esc to cancel.`}
                disableHoverListener={!displayTooltips}
              >
                <IconButton
                  color="primary"
                  size="large"
                  onClick={() => handleIconClick('draw_text')}
                  sx={{
                    p: 0.5, mx: 0.25, flexShrink: 0,
                    // Theme tokens, like the ordinary icons' ring above -- the pale lilac wash
                    // reads as disabled on the dark bar rather than as engaged.
                    ...(isActive ? {
                      bgcolor: 'action.selected',
                      border: '2px solid',
                      borderColor: 'secondary.main',
                      borderRadius: 1,
                    } : {}),
                  }}
                >
                  {/* Through `legible` like every other drawn glyph. This branch renders its SVG
                      INLINE rather than from `icon.component`, so it bypassed the lift entirely
                      and was one of the two left under 3:1 after the first measurement. */}
                  {legible(
                  <svg width="28" height="28" viewBox="0 0 28 28">
                    {/* The glyph itself scales with the queued size, so the choice is legible at a
                        glance without reading the badge. */}
                    <text
                      x="11" y="21" textAnchor="middle" fontWeight="bold"
                      fill="#7b1fa2" fontFamily="sans-serif"
                      fontSize={size.short === 'S' ? 14 : size.short === 'M' ? 18 : 22}
                    >T</text>
                    <text
                      x="23" y="24" textAnchor="middle" fontSize="10" fontWeight="bold"
                      fill="#4a148c" fontFamily="sans-serif"
                    >{size.short}</text>
                  </svg>)}
                </IconButton>
              </Tooltip>
            );
          }
          if (icon.isDrawingColorPicker) {
            return (
              <Tooltip
                arrow
                key={idx}
                placement="bottom"
                title={`Colour for new drawings and text (${props.activeDrawingColor}). Existing drawings keep their own colour.`}
                disableHoverListener={!displayTooltips}
              >
                <Box
                  component="label"
                  sx={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    width: 34, height: 34, mx: 0.5, borderRadius: '50%', cursor: 'pointer',
                    // A ring rather than a plain square, so it reads as a colour control and not
                    // as another icon button that happens to be tinted.
                    // The ring takes the BAR's colour, not white: a white ring on the dark bar
                    // is a brighter mark than the swatch it is meant to frame.
                    border: '3px solid',
                    borderColor: 'background.paper',
                    outline: '2px solid',
                    outlineColor: 'divider',
                    boxShadow: '0 0 0 1px rgba(0,0,0,0.15)',
                    boxSizing: 'border-box', flexShrink: 0,
                    // Falls back to the default rather than the bare prop. bgcolor: undefined
                    // renders a transparent circle, which looks exactly like the control being
                    // absent — the failure mode is indistinguishable from "not there", so it must
                    // not depend on a prop arriving.
                    bgcolor: props.activeDrawingColor || DEFAULT_DRAWING_COLOR,
                  }}
                >
                  {/* A native colour input: it gives the OS picker and full-spectrum choice for
                      free, and stays keyboard accessible, which a grid of swatches would not. */}
                  <input
                    type="color"
                    aria-label="Drawing colour"
                    value={props.activeDrawingColor || DEFAULT_DRAWING_COLOR}
                    onChange={e => {
                      if (typeof props.onDrawingColorChange === 'function') {
                        props.onDrawingColorChange(e.target.value);
                      }
                    }}
                    style={{ opacity: 0, width: 0, height: 0, border: 0, padding: 0 }}
                  />
                </Box>
              </Tooltip>
            );
          }
          const isActiveDrawTool = icon.action && icon.action.startsWith('draw_') && props.drawingMode === icon.action;
          // Any action the app reports as ON gets the same treatment a live drawing tool gets.
          // A second bespoke style per toggle is how two active buttons end up looking different.
          const isActiveToggle = isActiveDrawTool
            || (icon.action && Array.isArray(props.activeActions)
                && props.activeActions.includes(icon.action));
          return (
          <Tooltip arrow key={idx} title={tooltipForProduct(icon.tooltip, product)}
            placement="bottom" disableHoverListener={!displayTooltips}>
            <IconButton
              color="primary"
              size="large"
              onClick={icon.action ? () => {
                if (icon.action === 'open_horizontal_line_dialog') {
                  console.log('[DEBUG] 3-line toolbar icon clicked');
                }
                handleIconClick(icon.action);
              } : undefined}
              sx={{
                p: 0.5, mx: 0.25,
                // The ACTIVE ring, through theme tokens. It was `#e3f2fd` on `#1976d2`, a pale
                // blue wash that on the dark bar is the brightest thing in the row and reads as
                // the selected tool being disabled rather than engaged.
                ...(isActiveToggle ? {
                  bgcolor: 'action.selected',
                  border: '2px solid',
                  borderColor: 'primary.main',
                  borderRadius: 1,
                } : {}),
              }}
            >
              {icon.isImage
                ? <img src={icon.component} alt={icon.alt} style={{ width: icon.size || 36, height: icon.size || 36 }} />
                : legible(icon.component)}
            </IconButton>
          </Tooltip>
  );
        })}
        {/* THE ASTRO CATALOG & SCREENSHOT NOTES, second from last.
            ⚠ IT HAD NO ACTION ID UNTIL NOW, and so no gate could reach it. It is rendered as
            bare JSX outside the ICONS array, which means `isActionVisibleForProfile` was never
            consulted for it in any profile or product — it was unconditionally visible, and
            "hide this icon" was not expressible. `open_astro_catalog_notes` is that id; the two
            calls below are the same pair the ICONS loop makes, in the same order.

            The id is NOT in `actionDispatcher` because this button owns its own trigger
            component and dispatches nothing — the id exists to be GATED, which is the one thing
            the array's entries get for free and this one did not. */}
        {/*
          ⚠ ONE GATE NOW. This also consulted `isActionVisibleForProduct(...)` — the last
          `PRODUCT_DENIES` reader in the render path, kept while the catalog had to bypass it.
          The manifest answers the product question, so a second gate here would be a second
          answer, and the one that disagreed would be the one nobody tested.

          ⚠ AND THE BUTTON IS `MinimalMultiTabDialogTrigger`, whose name is historical: it IS the
          astro catalog and notes control, not a separate multi-tab tool. The registry briefly
          carried both ids for this one button.
        */}
        {showsControl('open_astro_catalog_notes') && (
          <Tooltip arrow title="View catalog of astro happenings for the upcoming week, month or year. Also add simple text notes to the chart if you are taking a screenshot" placement="bottom" disableHoverListener={!displayTooltips}>
            <span>
              <MinimalMultiTabDialogTrigger />
            </span>
          </Tooltip>
        )}
        {/*
          Elliott Wave Analysis.

          A dedicated button rather than an entry in the ICONS array, because it is the only
          toolbar control that runs work and has to show that it is running. The array renders
          every icon through one generic IconButton with no per-icon state, so a spinner there
          would mean threading a loading flag into a loop that has no concept of one.

          The icon is a five-wave impulse: three up-legs with two retracements, which is the shape
          the engine is looking for and reads as Elliott rather than as a generic squiggle.
        */}
        {/* ⚠ NAMED BY THE ADMIN MANIFEST ONLY, so it leaves the customer bar. Elliott Wave is a research tool and an admin reaches it from the Price & Geometry tab; no customer manifest names it, and under an allow-list that is what "not in the product" means. */}
        {showsTool('elliott_wave') && (
          <Tooltip
            arrow
            placement="bottom"
            title={props.elliottLoading
              ? 'Analysing wave structure…'
              : 'Elliott Wave Analysis — click a bar first; it is treated as the last bar, and the '
                + 'count, targets and projection are read as of it'}
            disableHoverListener={!displayTooltips}
          >
            <span>
              <IconButton
                size="small"
                onClick={() => { if (typeof props.onRunElliottAnalysis === 'function') props.onRunElliottAnalysis(); }}
                disabled={!!props.elliottLoading}
                sx={{ p: 0.5, mx: 0.25 }}
              >
                {props.elliottLoading ? (
                  <CircularProgress size={20} thickness={5} />
                ) : (
                  legible(
                <svg width="26" height="26" viewBox="0 0 32 32" aria-label="Elliott Wave Analysis">
                    <polyline
                      points="2,26 7,14 10,20 17,6 21,13 30,2"
                      fill="none" stroke="#1976d2" strokeWidth="2.4"
                      strokeLinejoin="round" strokeLinecap="round"
                    />
                    <circle cx="7" cy="14" r="1.7" fill="#1976d2" />
                    <circle cx="17" cy="6" r="1.7" fill="#1976d2" />
                    <circle cx="30" cy="2" r="1.7" fill="#1976d2" />
                  </svg>)
                )}
              </IconButton>
            </span>
          </Tooltip>
        )}

        {/*
          Chart view cycle — last icon.

          The icon shows the view a click LEADS TO, not the one in effect. That is how the
          two-state toggle already read (line view showed the candlestick icon), so the admin-only
          third state extends the rule rather than adding a second one:

            in line        -> candlestick icon   -> click gives candlesticks
            in candlestick -> eye-off icon       -> click hides the price   (admin only)
            in hidden      -> line icon          -> click restores the line (admin only)

          For a standard user nextChartView never returns 'hidden', so the eye-off icon never
          appears and the button keeps its original two-state behaviour. The cycle enforces that
          itself — this is presentation, not the guard.
        */}
        {/* Candles / line. Named by every manifest as view CHROME — a product that cannot switch chart type is not what any brief asked for. */}
        {showsTool('chart_type') && (
          <Tooltip arrow title={chartViewActionLabel(chartType, props.isAdmin)} placement="bottom" disableHoverListener={!displayTooltips}>
            <IconButton
              size="small"
              onClick={() => {
                onToggleChartType();
              }}
              sx={{ p: 0.5, mx: 0.25 }}
            >
              {(() => {
                switch (nextChartView(chartType, props.isAdmin)) {
                  case CHART_VIEW.CANDLESTICK:
                    return <BarChartIcon style={{ color: '#1976d2' }} fontSize="small" />;
                  case CHART_VIEW.HIDDEN:
                    return <VisibilityOffIcon style={{ color: '#1976d2' }} fontSize="small" />;
                  default:
                    return <ShowChartIcon style={{ color: '#1976d2' }} fontSize="small" />;
                }
              })()}
            </IconButton>
          </Tooltip>
        )}
        {/* Spacer for separation between active and disabled icons */}
        <Box sx={{ width: 44, minWidth: 0 }} />
        {showsTool('area_zoom') && (
          <Tooltip arrow title="Area Zoom" placement="bottom">
            <IconButton
              color="primary"
              size="large"
              onClick={onTriggerAreaZoom}
              sx={{ p: 0.5, mx: 0.25 }}
            >
              <img src="/images/zoom-png-2.png" alt="Area Zoom" style={{ width: 28, height: 28 }} />
            </IconButton>
          </Tooltip>
        )}
        {/*
          TrendPulse Pattern Prediction — administrators only.

          Two independent conditions, and they are not interchangeable:

            isAdmin        entitlement. Non-admins do not see the control at all, matching the
                           watermark toggle below rather than showing a permanently dead button
                           that invites clicking.
            hasAstroGraph  a real prerequisite, and it has been one in both directions. It was
                           dropped for a while on the grounds that the dialog's shape modes were
                           pure price geometry needing no overlay — true when they read candles,
                           and false since they were moved onto the TrendPulse curve, which is
                           where the shapes actually are. Both criteria now read that curve, so
                           without it the dialog can only say "run TrendPulse first". A control
                           whose every outcome is that sentence is better disabled, with the
                           reason in its tooltip.
        */}
        {props.isAdmin
          && showsControl('open_pattern_prediction_dialog') && (
          <Tooltip
            arrow
            placement="bottom"
            title={hasAstroGraph
              ? 'TrendPulse Pattern Prediction — detect shapes on the curve and score them against price (admin)'
              : 'TrendPulse Pattern Prediction (admin) — run TrendPulse first: the shapes are detected on its curve'}
          >
            {/* A disabled IconButton fires no events, so the tooltip needs an element that does. */}
            <span>
              <IconButton
                color="primary"
                size="large"
                disabled={!hasAstroGraph}
                onClick={() => handleIconClick('open_pattern_prediction_dialog')}
                sx={{ p: 0.5, mx: 0.25 }}
              >
                <TrendingUpIcon />
              </IconButton>
            </span>
          </Tooltip>
        )}
        {/*
          Screenshot sweep — administrators only, matching the tools around it.

          Disabled while a sweep is running rather than hidden: the run takes a second per image and
          a second click would start a competing sweep that fights the first one for the chart's
          zoom, so both sets would come out showing the wrong months. The tooltip carries where the
          files will go, because that is the one thing about this button that cannot be guessed
          from its icon — a page cannot write to a fixed path, so the answer is "wherever you point
          the folder dialog", and saying so before the click is cheaper than explaining it after.
        */}
        {/*
          ⚠ TWO GATES, AND THEY ASK DIFFERENT QUESTIONS. `props.isAdmin` is the PERMISSION — a
          sweep loads and renders the whole stored history, which is not something to leave one
          mis-click from a customer. `showsTool` is the PRODUCT: only the admin manifest names it.
          Collapsing either into the other is how a product list comes to grant a capability.
        */}
        {props.isAdmin && showsTool('sweep_screenshots') && (
          <Tooltip
            arrow
            placement="bottom"
            title={props.sweepRunning
              ? 'Screenshot sweep running…'
              : 'Screenshot sweep (admin) — save a PNG of each screenful, walking forward to the '
                + 'end of the data. Zoom to the width you want first; choose '
                + 'C:\\stockdata\\ClaudeScreenShots when the folder dialog opens.'}
          >
            {/* A disabled IconButton fires no events, so the tooltip needs an element that does. */}
            <span>
              <IconButton
                color="primary"
                size="large"
                disabled={!!props.sweepRunning}
                onClick={() => handleIconClick('sweep_screenshots')}
                sx={{ p: 0.5, mx: 0.25 }}
              >
                <PhotoCameraIcon />
              </IconButton>
            </span>
          </Tooltip>
        )}
        {/* ── OPERATOR FEATURES ────────────────────────────────────────────────────────────────
            Three quick-actions, gated on `operatorFeatures` as well as on isAdmin. The flag is a
            runtime read, so turning the set off in localStorage removes these on the next render
            without a rebuild — which is what makes the whole set judgeable by being looked at and
            then switched off. Deleting this block and its three cases above removes the buttons;
            reverting the commit removes everything.
        */}
        {props.isAdmin && props.operatorFeatures
          && showsControl('evaluate_validation_lines') && (
          <Tooltip arrow placement="bottom"
            title="Evaluate & Draw TrendPulse Validation Lines — score every boundary shelf against 1×ATR(14) over 5 bars and mark each as success or failure (admin)">
            <IconButton color="primary" size="large" sx={{ p: 0.5, mx: 0.25 }}
              onClick={() => handleIconClick('evaluate_validation_lines')}>
              <RuleIcon />
            </IconButton>
          </Tooltip>
        )}
        {props.isAdmin && props.operatorFeatures && (
          showsTool('copy_chart') && (
            <Tooltip arrow placement="bottom" title="Copy Chart (Clipboard) — high-resolution PNG of all panes">
              <IconButton color="primary" size="large" sx={{ p: 0.5, mx: 0.25 }}
                onClick={() => handleIconClick('copy_chart')}>
                <ContentCopyIcon />
              </IconButton>
            </Tooltip>
          )
        )}
        {/* Integral Delta auto-analysis.
            Admin-only, matching the other heavy tools beside it: one click loads the whole stored
            history and runs the ephemeris across it, which is not something to leave one mis-click
            away for every visitor. Its own component rather than an icon here, because it owns a
            running state the toolbar has no business knowing about. */}
        {/*
          ⚠ THE DAILY SCAN VIEWER MOVED TO THE ADMIN PANEL'S SYSTEM TAB (2026-11-01).

          It was `props.isAdmin &&` and a direct prop call — the last pair of controls, with the
          messages panel below, whose home was decided outside a manifest. A bare role gate says
          WHO may use a tool and never WHERE it lives, so the architecture could not see it at
          all. `admin.manifest.js` now names it under "Chart tools", and it gained a real
          dispatcher case because a panel row routes through `onAction`.

          `props.onOpenDailyScanViewer` is unchanged and still arrives from App — what moved is
          the button, not the handler.
        */}
        {/* The universe scan. Admin-only and beside the single-symbol analysis, because the two
            are the same engine at two scales - and this one refuses to run when today's scan
            already covers the newest data, so the cheap answer is the default. */}
        {/* ── THE DEV PROFILE TOGGLE — REMOVED 2026-11-01 ─────────────────────────────────
            ⚠ IT HAD BECOME A CONTROL THAT CHANGED NOTHING, which is this codebase's own worst
            category: "a switch that cannot be switched off is not a switch", in the other
            direction.

            It previewed another product's toolbar by selecting a PROFILE, and the toolbar stopped
            consulting profiles when it became manifest-driven. So from Stage 1 onward an operator
            could flip it, watch the bar not change, and reasonably conclude the toolbar was
            broken.

            The Admin Panel's PRODUCT SWITCHER is the surviving preview and it drives the manifest
            — one mechanism rather than two, which is the same argument `ManifestContext` makes
            for delegating to `readProductOverride` rather than inventing a second store. */}
        {/* ── RELOCATED, 2026-11-01 ───────────────────────────────────────────────────────
            Run Global Scan and Auto-Analysis moved to the Admin Panel's Cycle & Confluence tab.
            They were SOFT DELETED here from 2026-10-04 — hidden from every toolbar with no route
            to them at all — and relocation answers the same instruction better: still off the
            bar, and now with a button to press.

            ⚠ THE JSX IS GONE RATHER THAN GATED, which is the opposite of what this file does for
            a relocated ACTION. An action's ICONS entry stays and the renderer filters it, so
            there is exactly one declaration. These two are COMPONENTS that own their dialog and
            their progress state, and they are MOUNTED where they are declared — so leaving a
            gated copy here and adding one to the panel would be two mount points for one
            control, with whichever condition was wrong mounting it twice or not at all.

            They are rendered by `AdminPanelDialog`'s `slots` prop now — which this component
            still supplies, because this component is what renders the panel. The four props are
            unchanged; they just reach a different child. */}
        {props.isAdmin && props.operatorFeatures && (
          showsTool('save_screenshot') && (
            <Tooltip arrow placement="bottom" title="Save Screenshot — high-resolution PNG of the visible range, downloaded">
              <IconButton color="primary" size="large" sx={{ p: 0.5, mx: 0.25 }}
                onClick={() => handleIconClick('save_screenshot')}>
                <DownloadIcon />
              </IconButton>
            </Tooltip>
          )
        )}
        {/* Permission AND product, as above: admin-only, and only the admin manifest names it. */}
        {props.isAdmin && showsTool('watermark_toggle') && (
          <Tooltip arrow title={
            !props.watermarkOverride ? 'Hide watermark' :
            props.watermarkOverride.hidden ? 'Show small watermark' :
            'Restore default watermark'
          } placement="bottom">
            <IconButton
              color="primary"
              size="large"
              onClick={() => { if (typeof props.onToggleWatermark === 'function') props.onToggleWatermark(); }}
              sx={{ p: 0.5, mx: 0.25 }}
            >
              {!props.watermarkOverride ? <VisibilityIcon /> :
               props.watermarkOverride.hidden ? <VisibilityOffIcon /> :
               <TextDecreaseIcon />}
            </IconButton>
          </Tooltip>
        )}
        {/* X1. Named by every manifest, beside X — undo one drawing, then clear the chart. */}
        {showsTool('undo_last') && (
          <Tooltip
            arrow
            placement="bottom"
            title={
              drawingCount > 0
                ? `Delete Last (X₁) — removes the most recent drawing or study${nextUndoSize > 1 ? ` (${nextUndoSize} overlays)` : ''}. ${drawingCount} on the chart. Shortcut: Ctrl+Z or Delete.`
                : 'Delete Last (X₁) — nothing to undo. Shortcut: Ctrl+Z or Delete.'
            }
          >
            {/* Wrapped: a disabled MUI button fires no events, so the Tooltip would never show and
                there would be nothing explaining why the icon is greyed out. */}
            <span>
              <IconButton
                color="primary"
                size="large"
                disabled={drawingCount === 0}
                onClick={() => { if (typeof props.onDeleteLastDrawing === 'function') props.onDeleteLastDrawing(); }}
                sx={{ p: 0.5, mx: 0.25, ...(drawingCount === 0 ? { opacity: 0.35 } : {}) }}
              >
                {legible(
                  <svg width="28" height="28" viewBox="0 0 28 28">
                    <line x1="6" y1="7" x2="19" y2="20" stroke="#d32f2f" strokeWidth="2.5" strokeLinecap="round" />
                    <line x1="19" y1="7" x2="6" y2="20" stroke="#d32f2f" strokeWidth="2.5" strokeLinecap="round" />
                    {/* The subscript 1 is what distinguishes this from Clear All at a glance. */}
                    <text x="23" y="24" textAnchor="middle" fontSize="12" fontWeight="bold"
                          fill="#b71c1c" fontFamily="sans-serif">1</text>
                  </svg>)}
              </IconButton>
            </span>
          </Tooltip>
        )}
        {showsTool('clear_all') && (
          <Tooltip arrow title="Remove all astro studies from the chart and get the chart ready to add different studies. Does not remove all technical studies" placement="bottom">
            <IconButton
              color="primary"
              size="large"
              onClick={() => handleIconClick('clear_all_overlays')}
              sx={{ p: 0.5, mx: 0.25 }}
            >
              <ClearIcon />
            </IconButton>
          </Tooltip>
        )}
        <Box sx={{ width: 44, minWidth: 0 }} />
        {/* Group of inactive icons on the right */}
                {/*
          ⚠ THE SIX UNBUILT PLACEHOLDERS, and `unbuilt_placeholder` is an action id no manifest
          can name — so `showsAction` is always false and none of them mounts.

          Gated rather than deleted. Each is a `disabled` IconButton with pointer events off and
          no handler at all, so it never did anything; what the glyph and the tooltip carry is the
          only record that the feature was planned, and that is worth more in the tree than a
          commented-out block, which stops being compiled and linted and decays until restoring it
          is a rewrite. Naming one in a manifest is how it comes back — at which point it needs a
          dispatcher case too, which is the reason it is not in the registry.
        */}
        {showsTool('unbuilt_placeholder') && (
          <Tooltip arrow title="Geometric Patterns (Not Implemented)" placement="bottom">
            <IconButton
              color="primary"
              size="large"
              disabled
              sx={{ p: 0.5, mx: 0.25, opacity: 0.4, pointerEvents: 'none' }}
            >
              <ArchitectureIcon />
            </IconButton>
          </Tooltip>
        )}
                {showsTool('unbuilt_placeholder') && (
          <Tooltip arrow title="Historical Ephemeris (Not Implemented)" placement="bottom">
            <IconButton
              color="primary"
              size="large"
              disabled
              sx={{ p: 0.5, mx: 0.25, opacity: 0.4, pointerEvents: 'none' }}
            >
              <HistoryEduIcon />
            </IconButton>
          </Tooltip>
        )}
                {showsTool('unbuilt_placeholder') && (
          <Tooltip arrow title="Custom Formulas (Not Implemented)" placement="bottom">
            <IconButton
              color="primary"
              size="large"
              disabled
              sx={{ p: 0.5, mx: 0.25, opacity: 0.4, pointerEvents: 'none' }}
            >
              <FunctionsIcon />
            </IconButton>
          </Tooltip>
        )}
                {showsTool('unbuilt_placeholder') && (
          <Tooltip arrow title="Cycle Analysis (Not Implemented)" placement="bottom">
            <IconButton
              color="primary"
              size="large"
              disabled
              sx={{ p: 0.5, mx: 0.25, opacity: 0.4, pointerEvents: 'none' }}
            >
              <ChangeHistoryIcon />
            </IconButton>
          </Tooltip>
        )}
                {showsTool('unbuilt_placeholder') && (
          <Tooltip arrow title="Track planets" placement="bottom">
            <IconButton
              color="primary"
              size="large"
              disabled
              sx={{ p: 0.5, mx: 0.25, opacity: 0.4, pointerEvents: 'none' }}
            >
              <TrackChangesIcon />
            </IconButton>
          </Tooltip>
        )}
                {showsTool('unbuilt_placeholder') && (
          <Tooltip arrow title="Planet Group Study" placement="bottom">
            <IconButton
              color="primary"
              size="large"
              disabled
              sx={{ p: 0.5, mx: 0.25, opacity: 0.4, pointerEvents: 'none' }}
            >
              <PublicIcon />
            </IconButton>
          </Tooltip>
        )}
        {/* Spacer to push messages icon to far right */}
        <Box flexGrow={1} />

        {/*
          THE STUDY-OUTPUT DRAWER, ON THE BAR — beside the search and the two clears.

          ⚠ A SECOND ROUTE, NOT THE FIRST. `MessagesPanelButton` is in the header on all four
          products and was verified present here; this is the same drawer reached from the toolbar
          strip, which is a different region of the screen and the one a reader is already using
          when a study finishes. Only the integralDelta manifest names `messages_panel`, so this is
          the only product it mounts on.

          ⚠ IT DRIVES `setMessagesOpen` DIRECTLY, like `area_zoom` and `onDeleteLastDrawing` either
          side of it, rather than dispatching. `open_messages_panel` deliberately only ever SHOWS —
          *"'Show', not 'toggle': the toolbar button it replaced always opened the panel"* — which
          is right for the Admin Panel ROW that dispatches it and wrong for a persistent control
          that must be able to close what it opened. Dispatching here would have meant either a
          button that cannot close, or changing that case and silently altering the admin row.

          <p>No new prop was needed: `setMessagesOpen` is ALREADY required by this component, so
          the functional updater is the whole of it. Adding an `onToggleMessagesPanel` beside it
          would have been a second way to reach one piece of state.</p>

          ⚠ AND IT SHOWS ITS STATE. `messagesOpen` is already a required prop of this component, so
          a filled background and `aria-pressed` cost nothing — a toggle with no state readout is
          the control a reader presses twice.
        */}
        {showsTool('messages_panel') && (
          <Tooltip
            arrow
            placement="bottom"
            title="Toggle Message Panel / Study Results"
          >
            <IconButton
              color="primary"
              size="large"
              aria-label="Toggle Message Panel"
              aria-pressed={Boolean(props.messagesOpen)}
              data-testid="toolbar-messages-panel-button"
              onClick={() => props.setMessagesOpen((v) => !v)}
              sx={{
                p: 0.5,
                mx: 0.25,
                flexShrink: 0,
                bgcolor: props.messagesOpen ? 'rgba(25,118,210,0.16)' : 'transparent',
                '&:hover': { bgcolor: 'rgba(25,118,210,0.24)' },
                '&:active': { bgcolor: 'rgba(25,118,210,0.32)' },
              }}
            >
              {/* The clipboard the header uses, so one drawer is one glyph wherever it is reached
                  from — and NOT an envelope, which would read as `message_centre`. */}
              {legible(<AssignmentOutlinedIcon sx={{ fontSize: 28 }} />)}
            </IconButton>
          </Tooltip>
        )}

        {/* Bar Floater Selection Icon — the astro toggle.
            REMOVED FROM THE DOM in short bar mode, not merely disabled. Its own tooltip advertises
            "Planetary Aspects, or Planet Positions", so a greyed-out button still tells a reader
            that astrological data exists behind it — which is the thing the preset is there to not
            expose. The dialog it opens is unreachable by any other route. */}
        {props.barMode !== SHORT && (
          <Tooltip
            arrow
            placement="bottom"
            title={barFloaterTooltip(product)}
            // ⚠ `pre-line`, OR THE BLANK LINE IS NOT A BLANK LINE. MUI renders the title into a
            // normal-whitespace span, which collapses `\n\n` to a single space — the integralDelta
            // tooltip would read "OHLCV RSI" on one line, which is a different claim: it would
            // look like one combined readout rather than the two sections the floater prints.
            slotProps={{ tooltip: { sx: { whiteSpace: 'pre-line', textAlign: 'center' } } }}
          >
            <IconButton
              onClick={() => setBarFloaterDialogOpen(true)}
              sx={{ ml: 1 }}
              aria-label={barFloaterAriaLabel(product)}
            >
              <ViewListIcon style={{ color: '#1976d2' }} />
            </IconButton>
          </Tooltip>
        )}

        {/* Cycles Dialog Icon */}
        {/* ⚠ NAMED BY THE ADMIN MANIFEST ONLY, so it leaves the customer bar — flagged rather than assumed. It is a two-click measuring tool a customer might reasonably want; the brief's tool lists do not include it, and adding one id to a manifest is how it comes back. */}
        {showsTool('cycles_dialog') && (
          <Tooltip arrow title="Zoom the chart and click the two bars that you think define a cycle. Open the dialog and select type parameters (color, fib ratios if desired and line thickness) and click &quot;Display&quot; to see the chart updated" placement="bottom">
            <IconButton
              onClick={onOpenCyclesDialog}
              sx={{ ml: 1 }}
              aria-label="Open Cycles Dialog"
            >
              <img src="/images/vertical-lines.png" alt="Cycles Dialog" style={{ width: 36, height: 36 }} />
            </IconButton>
          </Tooltip>
        )}

        {/* Messages Panel Icon — ADMIN ONLY.
            Removed from the DOM rather than disabled, like the astro toggle above it. What it
            opens is the study-output panel: a greyed-out envelope still tells a customer that a
            reporting surface exists behind it, which is the thing the restriction is for. The
            dialog itself stays mounted below and is still opened by the studies that populate
            it, so a study run by an admin is unaffected. */}
        {/*
          ⚠ AND THE MESSAGES PANEL WITH IT — same move, same reason. Admin Panel → System tab →
          "Chart tools". The dialog it opens stays mounted below and is still populated and shown
          by the studies themselves, so a study run by an admin is unaffected; only the way a
          reader opens it BY HAND has moved.

          ⚠ ONE THING IS LOST AND IS WORTH STATING: the toolbar button coloured itself
          `secondary` while the panel was open, so the bar carried the panel's state. A text row
          in a dialog cannot, and nothing else shows it. That is the cost of the move.
        */}
      </Box>
      {/* Planet Number Popover Dialog */}
      <Popover
        open={planetNumberDialogOpen}
        onClose={() => setPlanetNumberDialogOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { p: 1, display: 'flex', flexDirection: 'row', borderRadius: 2, boxShadow: 3, bgcolor: 'background.paper', border: '1px solid #aaa', minWidth: 0 } }}
      >
        {[1,2,3,4,5,6,7,8,9].map(n => (
          <Box key={n} sx={{ width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', bgcolor: '#f5f5f5', m: 0.5, fontWeight: 'bold', fontSize: 18, cursor: 'pointer', border: '1px solid #bbb', '&:hover': { bgcolor: '#e0e0e0' } }}>{n}</Box>
        ))}
      </Popover>

      <MessagesPanelDialog
        open={props.messagesOpen}
        onClose={() => props.setMessagesOpen(false)}
        tabContents={props.messagesTabContents}
        studyTitle={props.studyTitle}
        journalMarkdown={props.journalMarkdown}
        onJournalChange={props.onJournalChange}
        onJournalReset={props.onJournalReset}
        journalEditing={props.journalEditing}
        onJournalEditingChange={props.onJournalEditingChange}
        tabIndex={props.messagesTabIndex}
        onTabIndexChange={props.onMessagesTabIndexChange}
      />
      {/* Aspects Area Highlight Dialog integration */}
      {/*
      <AspectsAreaHighlightDialog
        open={...}
        onClose={...}
        onRun={handleAspectsAreaHighlightRun}
        barData={...}
      />
      */}

    {/* Vedic Aspects Study Dialog */}
        <MOAStrategyDialog
      /* EITHER source may open it: TabPanel's own toolbar state, or App's command state. The
         two are ORed rather than one replacing the other, because the toolbar path predates
         the command path and must keep working untouched. */
      open={moaStrategyDialogOpen || !!props.moaOpen}
      onClose={() => {
        setMoaStrategyDialogOpen(false);
        if (typeof props.onMoaClose === 'function') props.onMoaClose();
      }}
      initialConfig={props.moaInitialConfig || null}
      barData={props.barData}
      extendedBarData={props.extendedBarData}
      setMessagesTabContents={props.setMessagesTabContents}
      setMessagesOpen={props.setMessagesOpen}
      setStudyTitle={props.setStudyTitle}
      onJournalAppend={props.onJournalAppend}
    />
    <BarFloaterSelectionDialog
      open={barFloaterDialogOpen}
      onClose={() => setBarFloaterDialogOpen(false)}
      currentMode={props.barFloaterMode || 'ohlcv'}
      isAdmin={!!props.isAdmin}
      // The dialog filters its own options by product too -- the tooltip is not the gate.
      product={product}
      onApply={(mode) => {
        if (typeof props.onBarFloaterModeChange === 'function') props.onBarFloaterModeChange(mode);
      }}
    />
    <VedicAspectsStudyDialog 
      open={vedicAspectsOpen}
      onClose={() => setVedicAspectsOpen(false)}
      barData={props.barData}
      extendedBarData={props.extendedBarData}
      onJournalAppend={props.onJournalAppend}
      onRun={({ stats, futureDates }) => {
            if (typeof props.setMessagesTabContents === 'function') {
              const { formatMessagesPanelTabs } = require('../utils/messagesPanelUtils');
              const tabs = formatMessagesPanelTabs(stats, futureDates);
              props.setMessagesTabContents(tabs);
            }
            if (typeof props.setMessagesOpen === 'function') props.setMessagesOpen(true);
          }}
    />
      {/* THE ADMIN PANEL. Rendered here rather than in App.js because `handleIconClick` lives
          here -- the panel runs each relocated tool through the toolbar's OWN dispatcher, so a
          relocated tool does exactly what its icon did and there is no second routing table to
          drift out of step.

          `priceHidden` / `onTogglePriceHidden` are deliberately NOT passed: the panel's
          "Hide the price series" switch was soft-deleted on request. Both props are still
          declared below and the mechanic in utils/chartViewMode.js is untouched, so restoring
          the control is re-adding a switch and one prop -- not rebuilding a feature. */}
      <AdminPanelDialog
        open={adminPanelOpen}
        isAdmin={!!props.isAdmin}
        onClose={() => setAdminPanelOpen(false)}
        onAction={handleIconClick}
        /*
         * ⚠ THE TWO BATCH SCANS ARE COMPONENTS, NOT ACTIONS — see the relocation note in
         * `adminPanelTools.js`. Each owns its button, its dialog and its progress state, so the
         * panel renders the component itself rather than a Button that calls `onAction`.
         *
         * Built inline rather than memoised: these are two elements whose props already change on
         * every symbol, and a memo keyed on them would cost more than it saved.
         */
        slots={{
          run_global_scan: (
            <GlobalScanButton marketId={props.scanMarketId} onError={props.onAnalysisError} />
          ),
          auto_analysis: (
            <ChartAnalysisButton
              symbol={props.analysisSymbol}
              onComplete={props.onAnalysisComplete}
              onError={props.onAnalysisError}
            />
          ),
        }}
        onZoomFirstYear={props.onZoomFirstYear}
        tradeCardCount={props.tradeCardCount}
        openTradeCardCount={props.openTradeCardCount}
        onSetAllTradeCards={props.onSetAllTradeCards}
        yearCount={props.yearCount}
        summariesShown={props.summariesShown}
        onSetYearlySummaries={props.onSetYearlySummaries}
        onEnterPresentationMode={props.onEnterPresentationMode}
        onOpenDeepPivot={props.onOpenDeepPivot}
        onOpenCelestialAspects={props.onOpenCelestialAspects}
        onRunMidpointTrineScan={props.onRunMidpointTrineScan}
        onRunEclipseStudy={props.onRunEclipseStudy}
        onRunMarketScanner={props.onRunMarketScanner}
        onOpenPlanetaryAnalog={props.onOpenPlanetaryAnalog}
        onOpenPivotCommonalities={props.onOpenPivotCommonalities}
        onOpenChartEndDates={props.onOpenChartEndDates}
        // Forwarded so the panel can DISABLE the tools that refuse to guess an anchor, and say
        // why. The dispatch itself still goes through `onAction`/`handleIconClick`.
        anchorIndex={props.skGridAnchorIndex}
        // The product switcher. `product` is the EFFECTIVE target (already role-tested by App)
        // and `buildProduct` is what the bundle committed -- the panel shows both, because an
        // administrator inspecting another product needs to know they are not seeing what ships.
        product={product}
        buildProduct={ACTIVE_PRODUCT}
        onProductChange={props.onProductChange}
        ctsCooldownBars={props.ctsCooldownBars}
        onCtsCooldownBarsChange={props.onCtsCooldownBarsChange}
      />
    </>
  );
};

TabPanel.propTypes = {
  /** Whether the price series is currently drawn invisibly — the Admin Panel's switch. */
  priceHidden: PropTypes.bool,
  onTogglePriceHidden: PropTypes.func,
  /** Zooms the chart to its first calendar year; returns `{year, from, to, bars}` or null. */
  onZoomFirstYear: PropTypes.func,
  /** How many trade cards exist, and how many are open, so the panel can label its button. */
  tradeCardCount: PropTypes.number,
  openTradeCardCount: PropTypes.number,
  /** Opens or closes every trade card; returns how many are now open. */
  onSetAllTradeCards: PropTypes.func,
  /** How many calendar years the drawn trades span, for the plaque button's label. */
  yearCount: PropTypes.number,
  summariesShown: PropTypes.bool,
  /** Shows or hides the yearly summary plaques; returns how many are now drawn. */
  onSetYearlySummaries: PropTypes.func,
  /** Hides the toolbar entirely for a screenshot. P or Esc comes back. */
  onEnterPresentationMode: PropTypes.func,
  /** Opens the Deep Pivot Research view. Admin only. */
  onOpenDeepPivot: PropTypes.func,
  onOpenCelestialAspects: PropTypes.func,
  /** Runs the midpoint trine scan on the loaded range. Admin only, server-enforced. */
  onRunMidpointTrineScan: PropTypes.func,
  onRunEclipseStudy: PropTypes.func,
  onRunMarketScanner: PropTypes.func,
  onOpenPlanetaryAnalog: PropTypes.func,
  onOpenPivotCommonalities: PropTypes.func,
  onOpenChartEndDates: PropTypes.func,
  /** Runs the Spiral Calendar convergence scan for the loaded symbol. Optional. */
  onSpiralReversalScan: PropTypes.func,
  /** Runs the Critical Timing Scan at the given orb, without opening a dialog. */
  onRunCts: PropTypes.func,
  /** Draws one of the two quick-run cycle studies as plain shaded intervals. */
  onRunCycleBand: PropTypes.func,
  /** Turns bar-replay on or off. */
  onToggleSimulate: PropTypes.func,
  /** Actions currently ON, drawn with the active-toggle style. */
  activeActions: PropTypes.arrayOf(PropTypes.string),
  /** 'short' hides two runs of icons; 'long' shows the full bar. */
  barMode: PropTypes.string,
  /** Opens the Configuration dialog — the customer's own chart colours. */
  onOpenConfigurationDialog: PropTypes.func,
  /** Opens Messages. The button exists now; the messaging itself is the next step. */
  onOpenMessagesDialog: PropTypes.func,
  onOpenDynaManSimulator: PropTypes.func,
  /** Opens the Workbench Setup modal. Absent makes the toolbar icon inert, not broken. */
  onLaunchSimulator: PropTypes.func,
  /** Runs Chart Analytica to the LAST bar. The right-click path carries a bar of its own. */
  onChartAnalytica: PropTypes.func,
  /** Runs an engine and paints it. Receives the provider name: 'PTA' | 'PTB' | 'CTS'. */
  onRunEngine: PropTypes.func,
  /** Which toolbar profile is rendered. See config/toolbarProfiles.js. */
  toolbarProfile: PropTypes.string,
  /** Dev only: switch the rendered profile. Absent, the switch is not rendered. */
  onToolbarProfileChange: PropTypes.func,
  /**
   * The product target to gate the toolbar by. Absent, the BAKED target is used.
   *
   * <p>Not required, and that is the point: every existing caller and every test that renders
   * this component without it gates on the build's own target, which is the only value that
   * ships. See the note at the `product` declaration.
   */
  product: PropTypes.string,
  /** Admin only: switch the inspected product. Absent, the Admin Panel's switcher is read-only. */
  onProductChange: PropTypes.func,
  /** Bars a CTS mark silences after it. 0 draws every flagged bar. */
  ctsCooldownBars: PropTypes.number,
  onCtsCooldownBarsChange: PropTypes.func,
  onOpenPortfolioManager: PropTypes.func,
  // Optional, and absent means on: the toolbar keeps drawing labelled trendlines for any caller
  // that has not wired the toggle, which is how every drawn line behaved before it existed.
  onVerticalLinesAction: PropTypes.func.isRequired,
  onOpenPatternConfigDialog: PropTypes.func,
  onOpenHorizontalLineDialog: PropTypes.func.isRequired,
  showRSI: PropTypes.bool,
  onToggleRSI: PropTypes.func,
  onToggleMACD: PropTypes.func,
  barData: PropTypes.object,
  extendedBarData: PropTypes.object,
  theme: PropTypes.object,
  loading: PropTypes.bool,
  error: PropTypes.any,
  viewMode: PropTypes.string,
  chartColors: PropTypes.object,
  overlays: PropTypes.array,
  onOpenStrategyDesignDialog: PropTypes.func,
  onOpenStrategy2DesignDialog: PropTypes.func,
  onOpenTimeAndEventStrategyDialog: PropTypes.func,
  onOpenMOAStrategyDialog: PropTypes.func,
  /** App's own MOA open state, ORed with this component's. See the mount above. */
  moaOpen: PropTypes.bool,
  onMoaClose: PropTypes.func,
  moaInitialConfig: PropTypes.object,
  onOpenPlanetCharacteristicsDialog: PropTypes.func,
  onOpenAstroGraphDialog: PropTypes.func,
  /** Runs TrendPulse on the Custom preset without opening the configuration dialog. */
  onRunTrendPulse: PropTypes.func,
  onRunAstroGraphPatterns: PropTypes.func,
  onOpenPatternPredictionDialog: PropTypes.func,
  /** Captures the chart a screenful at a time, walking forward to the end of the data. */
  onSweepScreenshots: PropTypes.func,
  sweepRunning: PropTypes.bool,
  // ── OPERATOR FEATURES ─────────────────────────────────────────────────────────────────────────
  /** Runtime flag; false hides the three operator quick-actions with no rebuild. */
  operatorFeatures: PropTypes.bool,
  onEvaluateValidationLines: PropTypes.func,
  onCopyChart: PropTypes.func,
  onSaveScreenshot: PropTypes.func,
  /*
   * Still declared, and now consumed by the ADMIN PANEL rather than the toolbar. The two
   * batch-scan buttons were relocated on 2026-11-01; this component renders the panel, so it is
   * still the one that threads their props -- they simply arrive at a different child.
   */
  /** The symbol the auto-analysis runs on -- the one the chart is showing. */
  analysisSymbol: PropTypes.string,
  /** Which market the global scan covers: 1 = US, 2 = India. */
  scanMarketId: PropTypes.number,
  /** Opens the Daily Scan Viewer. */
  onOpenDailyScanViewer: PropTypes.func,
  onAnalysisComplete: PropTypes.func,
  onAnalysisError: PropTypes.func,
  isTimeAndEventStrategyDialogOpen: PropTypes.bool,
  onCloseTimeAndEventStrategyDialog: PropTypes.func,
    onOpenAggregateAreaHighlightDialog: PropTypes.func,
  onOpenStrategyReportStudyDialog: PropTypes.func.isRequired,
  onOpenStrategyReportStudyBDialog: PropTypes.func.isRequired,
  onOpenControlsDialog: PropTypes.func.isRequired,
  onOpenPlanetFollowStudyDialog: PropTypes.func.isRequired,
  onOpenAspectsAreaHighlightDialog: PropTypes.func.isRequired,
  onOpenDeclinationStudyDialog: PropTypes.func.isRequired,
  onOpenPlanetaryLinesDialog: PropTypes.func.isRequired,
  onOpenAverageLongitudeDialog: PropTypes.func,
  onOpenVisualUniverseDialog: PropTypes.func.isRequired,
  onOpenIntradayAnimationDialog: PropTypes.func,
  onOpenChartComparisonDialog: PropTypes.func,
  onOpenAreaHighlightDialog: PropTypes.func.isRequired,
  onOpenPlanetGroupDialog: PropTypes.func.isRequired,
  // 'hidden' is admin-only; App.js normalises before passing, so a standard user never sees it.
  chartType: PropTypes.oneOf(['candlestick', 'line', 'hidden']).isRequired,
  // Which Messages panel tab is showing. Owned by App so a study can open the panel on the tab it
  // wrote to — a Journal-only study opening on Summary looks like it produced nothing.
  messagesTabIndex: PropTypes.number,
  onMessagesTabIndexChange: PropTypes.func,
  // Elliott Wave Analysis: the click handler, and whether a run is in flight so the button can
  // show a spinner and refuse a second click while the first is still going.
  onRunElliottAnalysis: PropTypes.func,
  elliottLoading: PropTypes.bool,
  onToggleChartType: PropTypes.func.isRequired,
  messagesTabContents: PropTypes.arrayOf(PropTypes.node).isRequired,
  setMessagesTabContents: PropTypes.func.isRequired,
  messagesOpen: PropTypes.bool.isRequired,
  setMessagesOpen: PropTypes.func.isRequired,
  studyTitle: PropTypes.string,
  setStudyTitle: PropTypes.func,
  journalMarkdown: PropTypes.string,
  onJournalChange: PropTypes.func,
  onJournalReset: PropTypes.func,
  onJournalAppend: PropTypes.func,
  onOpenCyclesDialog: PropTypes.func,
  onTriggerAreaZoom: PropTypes.func,
  drawingMode: PropTypes.string,
  nextTextSize: PropTypes.shape({ short: PropTypes.string, label: PropTypes.string, px: PropTypes.number }),
  journalEditing: PropTypes.bool,
  onJournalEditingChange: PropTypes.func,
  activeDrawingColor: PropTypes.string,
  onDrawingColorChange: PropTypes.func,
  onSetDrawingMode: PropTypes.func,
  onDeleteLastDrawing: PropTypes.func,
  /** Purges the drawn-cycle state so clear-all sticks. See the dispatcher's note. */
  onClearCycleRuns: PropTypes.func,
  onRunPivotAdditionSeries: PropTypes.func,
  onToggleRecentFibProjections: PropTypes.func,
  onToggleSk360: PropTypes.func,
  onToggleTimeProjection: PropTypes.func,
  onToggleSkGrid: PropTypes.func,
  /** The last clicked bar's index, or null. The panel disables SK-Grid without one. */
  skGridAnchorIndex: PropTypes.number,
  onOpenFixedCycleDialog: PropTypes.func,
  onRunFibPivotPairs: PropTypes.func,
  studyMode: PropTypes.bool,
  onExitStudyMode: PropTypes.func,
  barFloaterMode: PropTypes.string,
  onBarFloaterModeChange: PropTypes.func,
  isAdmin: PropTypes.bool,
  watermarkOverride: PropTypes.shape({
    hidden: PropTypes.bool,
    text: PropTypes.string,
    fontSize: PropTypes.number,
  }),
  onToggleWatermark: PropTypes.func,
};

export default TabPanel;
